import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, Download, Upload, Package, Mail, Globe,
  Layers, Loader2, ChevronDown, ChevronRight, Image as ImageIcon,
  Monitor, ToggleLeft, ToggleRight, Shield, CheckCircle2,
  Users, UserCog, Clock, Briefcase, RotateCcw, Eye, Ban, ShieldCheck,
  MapPin, Search, X, Save, RefreshCw, FileText, Filter, AlertCircle,
  // Category-icon set (used by getCategoryIcon below)
  HeartPulse, Dumbbell, UtensilsCrossed, Sparkles, Cpu, Shirt, Home,
  PawPrint, Car, BookOpen, Banknote, Plane, Gamepad2, ShoppingBag,
  Baby, Wrench, Flower2, Coffee, Pill, Tag,
} from "lucide-react";
import AppHeader from "./AppHeader";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TYPE_COLOR_MAP = {
  blue: { bg: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  amber: { bg: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  purple: { bg: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  pink: { bg: "bg-pink-100 text-pink-700", dot: "bg-pink-500" },
  green: { bg: "bg-green-100 text-green-700", dot: "bg-green-500" },
  red: { bg: "bg-red-100 text-red-700", dot: "bg-red-500" },
  teal: { bg: "bg-teal-100 text-teal-700", dot: "bg-teal-500" },
  indigo: { bg: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  orange: { bg: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  cyan: { bg: "bg-cyan-100 text-cyan-700", dot: "bg-cyan-500" },
};
const TYPE_COLORS_LIST = Object.keys(TYPE_COLOR_MAP);
const getTypeStyle = (color) => TYPE_COLOR_MAP[color] || TYPE_COLOR_MAP.blue;

// ─── Smart category-icon resolver (keyword library) ───
// Matches the FIRST keyword pattern that appears in the category name.
// Each entry maps a pattern array → { icon, color } so similar categories share visuals.
// Defaults to a neutral Tag icon when nothing matches.
const CATEGORY_ICON_RULES = [
  { patterns: ["pharma", "medic", "clinic", "doctor", "hospital", "chemist"], icon: Pill,             tone: "text-rose-500   bg-rose-50   border-rose-100" },
  { patterns: ["health", "wellbeing"],                                        icon: HeartPulse,       tone: "text-red-500    bg-red-50    border-red-100" },
  { patterns: ["fitness", "gym", "sport", "yoga", "supplement", "protein"],   icon: Dumbbell,         tone: "text-orange-500 bg-orange-50 border-orange-100" },
  { patterns: ["wellness", "spa"],                                            icon: Flower2,          tone: "text-pink-500   bg-pink-50   border-pink-100" },
  { patterns: ["beauty", "salon", "cosmetic", "makeup"],                      icon: Sparkles,         tone: "text-fuchsia-500 bg-fuchsia-50 border-fuchsia-100" },
  { patterns: ["cafe", "coffee", "tea"],                                      icon: Coffee,           tone: "text-amber-600  bg-amber-50  border-amber-100" },
  { patterns: ["food", "grocery", "restaurant", "bakery", "kitchen"],         icon: UtensilsCrossed,  tone: "text-amber-500  bg-amber-50  border-amber-100" },
  { patterns: ["fashion", "cloth", "apparel", "wear"],                        icon: Shirt,            tone: "text-indigo-500 bg-indigo-50 border-indigo-100" },
  { patterns: ["electronic", "tech", "gadget", "mobile", "computer"],         icon: Cpu,              tone: "text-sky-500    bg-sky-50    border-sky-100" },
  { patterns: ["home", "furniture", "decor", "interior"],                     icon: Home,             tone: "text-teal-500   bg-teal-50   border-teal-100" },
  { patterns: ["pet", "animal", "vet"],                                       icon: PawPrint,         tone: "text-yellow-600 bg-yellow-50 border-yellow-100" },
  { patterns: ["auto", "car", "bike", "vehicle", "garage"],                   icon: Car,              tone: "text-slate-600  bg-slate-50  border-slate-100" },
  { patterns: ["book", "school", "edu", "learn", "library"],                  icon: BookOpen,         tone: "text-blue-500   bg-blue-50   border-blue-100" },
  { patterns: ["finance", "bank", "atm", "money", "loan"],                    icon: Banknote,         tone: "text-emerald-500 bg-emerald-50 border-emerald-100" },
  { patterns: ["travel", "hotel", "tour", "trip"],                            icon: Plane,            tone: "text-cyan-500   bg-cyan-50   border-cyan-100" },
  { patterns: ["game", "entertain", "movie", "play"],                         icon: Gamepad2,         tone: "text-purple-500 bg-purple-50 border-purple-100" },
  { patterns: ["baby", "kid", "child", "toy"],                                icon: Baby,             tone: "text-pink-400   bg-pink-50   border-pink-100" },
  { patterns: ["repair", "service", "plumb", "electric", "tool"],             icon: Wrench,           tone: "text-zinc-500   bg-zinc-50   border-zinc-100" },
  { patterns: ["shop", "store", "retail", "market", "mart"],                  icon: ShoppingBag,      tone: "text-violet-500 bg-violet-50 border-violet-100" },
];
const DEFAULT_CATEGORY_ICON = { icon: Tag, tone: "text-gray-500 bg-gray-50 border-gray-100" };

export const getCategoryIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (!n || n === "uncategorized") return DEFAULT_CATEGORY_ICON;
  for (const rule of CATEGORY_ICON_RULES) {
    if (rule.patterns.some(p => n.includes(p))) return { icon: rule.icon, tone: rule.tone };
  }
  return DEFAULT_CATEGORY_ICON;
};

const CSV_URL_COLUMNS = [
  { key: "amazon_url", label: "Amazon" },
  { key: "flipkart_url", label: "Flipkart" },
  { key: "offline_url", label: "Offline" },
  { key: "blinkit_url", label: "Blinkit" },
  { key: "zepto_url", label: "Zepto" },
  { key: "wellversed.in_url", label: "Wellversed.in" },
  { key: "instamart_url", label: "Instamart" },
  { key: "meesho_url", label: "Meesho" },
];

const ProductTypeBadge = ({ type, productTypes }) => {
  const pt = (productTypes || []).find(p => p.name === type);
  const style = getTypeStyle(pt?.color);
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${style.bg}`}>
      {type || "Unit"}
    </span>
  );
};

// ─── BRANDS TAB (Card-style rows matching reference) ───
const BrandsTab = ({ platforms }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", icon_url: "", manager_email: "", platform_urls: {} });

  const fetchBrands = async () => {
    try {
      const r = await axios.get(`${API}/brands`);
      setBrands(r.data || []);
    } catch { toast.error("Failed to load brands"); }
    setLoading(false);
  };

  useEffect(() => { fetchBrands(); }, []);

  const openAdd = () => {
    setEditingBrand(null);
    setForm({ name: "", icon_url: "", manager_email: "", platform_urls: {} });
    setModalOpen(true);
  };

  const openEdit = (brand) => {
    setEditingBrand(brand);
    setForm({
      name: brand.name || "",
      icon_url: brand.icon_url || "",
      manager_email: brand.manager_email || "",
      platform_urls: brand.platform_urls || {},
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Brand name is required");
    setSaving(true);
    try {
      if (editingBrand) {
        await axios.put(`${API}/brands/${editingBrand.brand_id}`, form);
        toast.success("Brand updated");
      } else {
        await axios.post(`${API}/brands`, form);
        toast.success("Brand created");
      }
      setModalOpen(false);
      fetchBrands();
    } catch { toast.error("Failed to save brand"); }
    setSaving(false);
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Delete "${brand.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/brands/${brand.brand_id}`);
      toast.success("Brand deleted");
      fetchBrands();
    } catch { toast.error("Failed to delete brand"); }
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/brands/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brands.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2EB4AC]" /></div>;

  return (
    <>
      {/* Main card with dashed border like reference */}
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6" data-testid="brands-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Brands</h2>
            <p className="text-sm text-gray-500 mt-0.5">Manage brand options for task submissions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} className="text-sm gap-1.5 rounded-lg" data-testid="export-brands-btn">
              <Download className="w-4 h-4" /> Export
            </Button>
            <Button size="sm" onClick={openAdd} className="btn-accent text-sm gap-1.5 rounded-lg" data-testid="add-brand-btn">
              <Plus className="w-4 h-4" /> Add Brand
            </Button>
          </div>
        </div>

        {/* Brand cards list */}
        <div className="space-y-3" data-testid="brands-list">
          {brands.map(b => (
            <div key={b.brand_id} className="flex items-center justify-between px-5 py-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors" data-testid={`brand-row-${b.brand_id}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{b.name}</span>
                {b.manager_email && (
                  <span className="text-sm text-gray-400">({b.manager_email})</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(b)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-testid={`edit-brand-${b.brand_id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(b)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-brand-${b.brand_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {brands.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">No brands yet. Add one to get started.</div>
          )}
        </div>
      </div>

      {/* Brand Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingBrand ? "Edit Brand" : "Add Brand"}</DialogTitle>
            <DialogDescription>Fill in the brand details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Brand Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Wellversed" className="text-sm" data-testid="brand-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Icon URL</label>
              <div className="flex gap-2 items-center">
                <Input value={form.icon_url} onChange={e => setForm(f => ({ ...f, icon_url: e.target.value }))} placeholder="https://..." className="text-sm flex-1" data-testid="brand-icon-input" />
                {form.icon_url && <img src={form.icon_url} alt="preview" className="w-9 h-9 rounded-lg object-contain border border-gray-200" onError={e => e.target.style.display = 'none'} />}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Manager Email</label>
              <Input value={form.manager_email} onChange={e => setForm(f => ({ ...f, manager_email: e.target.value }))} placeholder="manager@wellversed.in" className="text-sm" data-testid="brand-email-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Platform Store URLs</label>
              <div className="space-y-2">
                {platforms.filter(p => p.active).map(p => (
                  <div key={p.platform_id} className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded flex items-center justify-center ${p.color_bg || 'bg-gray-100'}`}>
                      {p.icon_url ? <img src={p.icon_url} alt="" className="w-4 h-4" /> : <Globe className="w-3.5 h-3.5 text-gray-500" />}
                    </div>
                    <Input
                      value={form.platform_urls[p.platform_id] || ""}
                      onChange={e => setForm(f => ({ ...f, platform_urls: { ...f.platform_urls, [p.platform_id]: e.target.value } }))}
                      placeholder={`${p.name} store URL`}
                      className="text-xs flex-1"
                      data-testid={`brand-url-${p.platform_id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)} className="text-gray-500">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="save-brand-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── PRODUCTS TAB ───
const ProductsTab = ({ platforms }) => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedBrands, setExpandedBrands] = useState({});
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [form, setForm] = useState({ name: "", brand_id: "", product_type: "Unit", form_factor: "", ean: "", platform_urls: {}, image_url: "", decided_prices: {} });
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [typeManagerOpen, setTypeManagerOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeColor, setNewTypeColor] = useState("blue");
  const [editingType, setEditingType] = useState(null);
  const fileRef = useRef(null);

  const fetchProductTypes = async () => {
    try {
      const r = await axios.get(`${API}/product-types`);
      setProductTypes(r.data || []);
    } catch {}
  };

  const fetchData = async () => {
    try {
      const [pr, br] = await Promise.all([axios.get(`${API}/products`), axios.get(`${API}/brands`)]);
      setProducts(pr.data || []);
      setBrands(br.data || []);
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); fetchProductTypes(); }, []);

  const productsByBrand = {};
  products.forEach(p => {
    const bid = p.brand_id || "unassigned";
    if (!productsByBrand[bid]) productsByBrand[bid] = [];
    productsByBrand[bid].push(p);
  });

  const toggleBrand = (bid) => setExpandedBrands(prev => ({ ...prev, [bid]: !prev[bid] }));
  const expandAll = () => {
    const all = {};
    Object.keys(productsByBrand).forEach(bid => { all[bid] = true; });
    setExpandedBrands(all);
  };
  const collapseAll = () => setExpandedBrands({});

  const toggleBulkProduct = (pid) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const toggleBulkBrand = (brandProducts) => {
    const pids = brandProducts.map(p => p.product_id);
    const allIn = pids.every(id => bulkSelected.has(id));
    setBulkSelected(prev => {
      const next = new Set(prev);
      pids.forEach(id => allIn ? next.delete(id) : next.add(id));
      return next;
    });
  };
  const handleBulkDelete = async () => {
    if (!bulkSelected.size) return;
    if (!window.confirm(`Delete ${bulkSelected.size} product${bulkSelected.size !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/products/bulk-delete`, { product_ids: [...bulkSelected] });
      toast.success(`Deleted ${r.data.deleted} products`);
      setBulkSelected(new Set());
      fetchData();
    } catch { toast.error("Bulk delete failed"); }
    setBulkDeleting(false);
  };

  const openAdd = () => {
    setEditingProduct(null);
    setForm({ name: "", brand_id: brands[0]?.brand_id || "", product_type: "Unit", form_factor: "", ean: "", platform_urls: {}, image_url: "", decided_prices: {} });
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name || "",
      brand_id: product.brand_id || "",
      product_type: product.product_type || "Unit",
      form_factor: product.form_factor || "",
      ean: product.ean || "",
      platform_urls: product.platform_urls || {},
      image_url: product.image_url || "",
      decided_prices: product.decided_prices || {},
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!form.brand_id) return toast.error("Select a brand");
    setSaving(true);
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.product_id}`, form);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, form);
        toast.success("Product created");
      }
      setModalOpen(false);
      fetchData();
    } catch { toast.error("Failed to save product"); }
    setSaving(false);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/products/${product.product_id}`);
      toast.success("Product deleted");
      fetchData();
    } catch { toast.error("Failed to delete product"); }
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/products/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/products/template`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split("\n").filter(l => l.trim());
      if (lines.length < 2) return toast.error("CSV must have header + at least 1 row");
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        // Handle quoted CSV values properly
        const line = lines[i];
        const cols = [];
        let current = "";
        let inQuotes = false;
        for (let c = 0; c < line.length; c++) {
          if (line[c] === '"') { inQuotes = !inQuotes; }
          else if (line[c] === ',' && !inQuotes) { cols.push(current.trim()); current = ""; }
          else { current += line[c]; }
        }
        cols.push(current.trim());

        const row = {};
        headers.forEach((h, idx) => {
          const val = cols[idx] || "";
          if (h === "brand_name") row.brand_name = val;
          else if (h === "product_name") row.product_name = val;
          else if (h === "product_type") row.product_type = val || "Unit";
          else if (h === "form_factor") row.form_factor = val;
          else if (h === "ean") row.ean = val;
          else if (h === "amazon_url") row.amazon_url = val;
          else if (h === "flipkart_url") row.flipkart_url = val;
          else if (h === "offline_url") row.offline_url = val;
          else if (h === "blinkit_url") row.blinkit_url = val;
          else if (h === "zepto_url") row.zepto_url = val;
          else if (h === "wellversed.in_url") row["wellversed.in_url"] = val;
          else if (h === "instamart_url") row.instamart_url = val;
          else if (h === "meesho_url") row.meesho_url = val;
          else if (h === "decided_price_amazon") row.decided_price_amazon = val;
          else if (h === "decided_price_flipkart") row.decided_price_flipkart = val;
        });
        if (row.product_name) rows.push(row);
      }
      if (rows.length === 0) return toast.error("No valid product rows found");
      setImportData(rows);
      setImportModalOpen(true);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    if (!importData.length) return;
    setImporting(true);
    try {
      const r = await axios.post(`${API}/products/import`, { rows: importData });
      const msg = `Imported ${r.data.imported} products, skipped ${r.data.skipped}`;
      const brandsMsg = r.data.brands_created > 0 ? ` (${r.data.brands_created} new brands created)` : "";
      toast.success(msg + brandsMsg);
      setImportModalOpen(false);
      setImportData([]);
      fetchData();
    } catch { toast.error("Import failed"); }
    setImporting(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2EB4AC]" /></div>;

  return (
    <>
      {/* Bulk Import Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-5" data-testid="bulk-import-card">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4 text-[#2EB4AC]" />
            <span className="font-semibold text-sm text-gray-900">Bulk Import Products</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">Upload a CSV matching the template format to bulk-add products.</p>
          <div className="flex gap-2">
            <button onClick={handleDownloadTemplate} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors" data-testid="download-template-btn">
              <Download className="w-4 h-4" /> Download Template
            </button>
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#32B4AC] rounded-lg hover:bg-[#2a9d96] transition-colors" data-testid="upload-csv-btn">
              <Upload className="w-4 h-4" /> Upload CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileUpload} />
          </div>
        </div>
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-5" data-testid="bulk-images-card">
          <div className="flex items-center gap-2 mb-2">
            <ImageIcon className="w-4 h-4 text-[#2EB4AC]" />
            <span className="font-semibold text-sm text-gray-900">Bulk Import Images</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Coming soon — bulk update product images via CSV</p>
        </div>
      </div>

      {/* Products by Brand */}
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6" data-testid="products-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Products by Brand</h2>
            <p className="text-sm text-gray-500 mt-0.5">{products.length} products across {brands.length} brands</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={expandAll} className="text-xs text-[#2EB4AC] hover:underline" data-testid="expand-all-btn">Expand All</button>
            <button onClick={collapseAll} className="text-xs text-gray-400 hover:underline" data-testid="collapse-all-btn">Collapse All</button>
            {bulkSelected.size > 0 && (
              <Button variant="outline" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting} className="text-xs gap-1.5 rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300" data-testid="bulk-delete-btn">
                {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete {bulkSelected.size}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExport} className="text-xs gap-1.5 rounded-lg" data-testid="export-products-btn">
              <Download className="w-3.5 h-3.5" /> Export
            </Button>
            <Button size="sm" onClick={openAdd} className="btn-accent text-xs gap-1.5 rounded-lg" data-testid="add-product-btn">
              <Plus className="w-3.5 h-3.5" /> Add Product
            </Button>
          </div>
        </div>

        {brands.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Add brands first before adding products.</div>
        ) : (
          <div className="space-y-2">
            {brands.map(brand => {
              const bp = productsByBrand[brand.brand_id] || [];
              const isExpanded = expandedBrands[brand.brand_id];
              return (
                <div key={brand.brand_id} className="rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleBrand(brand.brand_id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors bg-white"
                    data-testid={`brand-section-${brand.brand_id}`}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      <span className="font-semibold text-sm text-gray-900">{brand.name}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{bp.length}</span>
                    </div>
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => toggleBulkBrand(bp)}
                        className={`text-[10px] px-2 py-0.5 rounded-md border transition-colors ${
                          bp.length > 0 && bp.every(p => bulkSelected.has(p.product_id))
                            ? "bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC]"
                            : "border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500"
                        }`}
                        data-testid={`bulk-select-brand-${brand.brand_id}`}
                      >
                        {bp.length > 0 && bp.every(p => bulkSelected.has(p.product_id)) ? "Deselect" : "Select"} all
                      </button>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 space-y-2 bg-gray-50/50">
                      {bp.length === 0 ? (
                        <p className="text-xs text-gray-400 pl-6 py-2">No products in this brand.</p>
                      ) : (
                        bp.map(product => (
                          <div key={product.product_id} className="flex items-center gap-3 pl-2 py-2.5 rounded-lg bg-white border border-gray-100 px-3 hover:border-gray-200 group transition-colors" data-testid={`product-row-${product.product_id}`}>
                            <button onClick={() => toggleBulkProduct(product.product_id)} className="shrink-0" data-testid={`bulk-check-${product.product_id}`}>
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                bulkSelected.has(product.product_id) ? "bg-[#32B4AC] border-[#32B4AC]" : "border-gray-300 hover:border-gray-400"
                              }`}>
                                {bulkSelected.has(product.product_id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                              </div>
                            </button>
                            {product.image_url ? (
                              <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-200" onError={e => e.target.style.display = 'none'} />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate block">{product.name}</span>
                              <div className="flex items-center gap-2">
                                {product.form_factor && <span className="text-[10px] text-gray-500">{product.form_factor}</span>}
                                {product.form_factor && product.ean && <span className="text-[10px] text-gray-300">|</span>}
                                {product.ean && <span className="text-[10px] text-gray-400">EAN: {product.ean}</span>}
                              </div>
                            </div>
                            {/* MAP Prices */}
                            {product.decided_prices?.amazon && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200 tabular-nums" data-testid={`product-map-amazon-${product.product_id}`}>
                                AMZ MAP: ₹{product.decided_prices.amazon}
                              </span>
                            )}
                            {product.decided_prices?.flipkart && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 tabular-nums" data-testid={`product-map-flipkart-${product.product_id}`}>
                                FK MAP: ₹{product.decided_prices.flipkart}
                              </span>
                            )}
                            <ProductTypeBadge type={product.product_type} productTypes={productTypes} />
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openEdit(product)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" data-testid={`edit-product-${product.product_id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDelete(product)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50" data-testid={`delete-product-${product.product_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Product Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription>Fill in the product details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Product Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Protein Bar" className="text-sm" data-testid="product-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Brand *</label>
              <select
                value={form.brand_id}
                onChange={e => setForm(f => ({ ...f, brand_id: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2EB4AC] bg-white"
                data-testid="product-brand-select"
              >
                <option value="">Select brand...</option>
                {brands.map(b => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Product Type</label>
                <button onClick={() => setTypeManagerOpen(true)} className="text-[10px] text-[#2EB4AC] hover:underline font-medium" data-testid="manage-types-btn">
                  Manage Types
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {productTypes.map(pt => {
                  const style = getTypeStyle(pt.color);
                  return (
                    <button
                      key={pt.name}
                      onClick={() => setForm(f => ({ ...f, product_type: pt.name }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                        form.product_type === pt.name ? 'border-[#2EB4AC] bg-[#E8F8F5]' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`product-type-${pt.name}`}
                    >
                      <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                      {pt.name}
                    </button>
                  );
                })}
                {productTypes.length === 0 && <span className="text-xs text-gray-400">No types defined. Click "Manage Types" to add.</span>}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Form Factor</label>
              <Input value={form.form_factor} onChange={e => setForm(f => ({ ...f, form_factor: e.target.value }))} placeholder="e.g. Powder, Capsule, Tablet, Liquid, Bar" className="text-sm" data-testid="product-form-factor-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">EAN</label>
              <Input value={form.ean} onChange={e => setForm(f => ({ ...f, ean: e.target.value }))} placeholder="e.g. 1234567890123" className="text-sm" data-testid="product-ean-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Platform URLs</label>
              <div className="space-y-2">
                {platforms.filter(p => p.active).map(p => (
                  <div key={p.platform_id} className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded flex items-center justify-center ${p.color_bg || 'bg-gray-100'}`}>
                      {p.icon_url ? <img src={p.icon_url} alt="" className="w-4 h-4" /> : <Globe className="w-3.5 h-3.5 text-gray-500" />}
                    </div>
                    <Input
                      value={form.platform_urls[p.platform_id] || ""}
                      onChange={e => setForm(f => ({ ...f, platform_urls: { ...f.platform_urls, [p.platform_id]: e.target.value } }))}
                      placeholder={`${p.name} product URL`}
                      className="text-xs flex-1"
                      data-testid={`product-url-${p.platform_id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Decided Prices (MAP)</label>
              <p className="text-[10px] text-gray-400 mb-2">Set the minimum advertised price per platform. Prices below this will be flagged as breaches.</p>
              <div className="grid grid-cols-2 gap-2">
                {platforms.filter(p => p.active && ["amazon", "flipkart"].includes(p.platform_id)).map(p => (
                  <div key={`dp-${p.platform_id}`} className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded flex items-center justify-center ${p.color_bg || 'bg-gray-100'}`}>
                      {p.icon_url ? <img src={p.icon_url} alt="" className="w-4 h-4" /> : <Globe className="w-3.5 h-3.5 text-gray-500" />}
                    </div>
                    <Input
                      type="number"
                      value={form.decided_prices[p.platform_id] || ""}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(f => ({
                          ...f,
                          decided_prices: {
                            ...f.decided_prices,
                            [p.platform_id]: val ? parseFloat(val) : undefined
                          }
                        }));
                      }}
                      placeholder={`${p.name} MAP (₹)`}
                      className="text-xs flex-1"
                      data-testid={`product-decided-price-${p.platform_id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Product Image URL</label>
              <div className="flex gap-2 items-center">
                <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="text-sm flex-1" data-testid="product-image-input" />
                {form.image_url && <img src={form.image_url} alt="preview" className="w-10 h-10 rounded-lg object-cover border border-gray-200" onError={e => e.target.style.display = 'none'} />}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)} className="text-gray-500">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="save-product-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import Products from CSV</DialogTitle>
            <DialogDescription>Review data before importing. Missing brands will be auto-created.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-700">
              Brands not found in the database will be <strong>automatically created</strong> during import.
            </div>
            {importData.length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Brand</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Form Factor</TableHead>
                      <TableHead>EAN</TableHead>
                      <TableHead>URLs</TableHead>
                      <TableHead>Amazon MAP</TableHead>
                      <TableHead>Flipkart MAP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importData.map((row, i) => {
                      const urlNames = CSV_URL_COLUMNS.filter(c => row[c.key]).map(c => c.label).join(", ");
                      return (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{row.brand_name}</TableCell>
                          <TableCell className="text-xs font-medium">{row.product_name}</TableCell>
                          <TableCell><ProductTypeBadge type={row.product_type || "Unit"} productTypes={productTypes} /></TableCell>
                          <TableCell className="text-xs text-gray-500">{row.form_factor || "-"}</TableCell>
                          <TableCell className="text-xs text-gray-500">{row.ean || "-"}</TableCell>
                          <TableCell className="text-[10px] text-gray-500">{urlNames || "-"}</TableCell>
                          <TableCell className="text-xs text-gray-500 tabular-nums">{row.decided_price_amazon ? `₹${row.decided_price_amazon}` : "-"}</TableCell>
                          <TableCell className="text-xs text-gray-500 tabular-nums">{row.decided_price_flipkart ? `₹${row.decided_price_flipkart}` : "-"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <span className="text-xs text-gray-500 mr-auto">{importData.length} rows to import</span>
            <Button variant="ghost" onClick={() => { setImportModalOpen(false); setImportData([]); }} className="text-gray-500">Cancel</Button>
            <Button onClick={handleImport} disabled={importing} className="btn-accent" data-testid="confirm-import-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Import {importData.length} Products
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Type Manager Dialog */}
      <Dialog open={typeManagerOpen} onOpenChange={setTypeManagerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Manage Product Types</DialogTitle>
            <DialogDescription>Add, edit, or remove product types.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Existing types */}
            <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
              {productTypes.map(pt => {
                const style = getTypeStyle(pt.color);
                const isEditing = editingType?.name === pt.name;
                return (
                  <div key={pt.name} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50/50 group">
                    {isEditing ? (
                      <>
                        <input
                          value={editingType.newName}
                          onChange={e => setEditingType(prev => ({ ...prev, newName: e.target.value }))}
                          className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-[#2EB4AC]"
                          autoFocus
                          data-testid={`edit-type-input-${pt.name}`}
                        />
                        <select
                          value={editingType.newColor}
                          onChange={e => setEditingType(prev => ({ ...prev, newColor: e.target.value }))}
                          className="text-[10px] px-1.5 py-1 border border-gray-200 rounded bg-white"
                        >
                          {TYPE_COLORS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                          onClick={async () => {
                            if (!editingType.newName.trim()) return;
                            try {
                              await axios.put(`${API}/product-types/${encodeURIComponent(pt.name)}`, { name: editingType.newName.trim(), color: editingType.newColor });
                              toast.success("Type updated");
                              setEditingType(null);
                              fetchProductTypes();
                              fetchData();
                            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                          }}
                          className="text-[#2EB4AC] hover:text-[#29958E] p-1"
                          data-testid={`save-type-${pt.name}`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingType(null)} className="text-gray-400 hover:text-gray-600 p-1">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <div className={`w-2.5 h-2.5 rounded-full ${style.dot} shrink-0`} />
                        <span className="text-xs font-medium text-gray-800 flex-1">{pt.name}</span>
                        <span className="text-[9px] text-gray-400 px-1.5 py-0.5 rounded bg-gray-100">{pt.color}</span>
                        <button
                          onClick={() => setEditingType({ name: pt.name, newName: pt.name, newColor: pt.color })}
                          className="text-gray-400 hover:text-gray-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`edit-type-${pt.name}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete type "${pt.name}"?`)) return;
                            try {
                              await axios.delete(`${API}/product-types/${encodeURIComponent(pt.name)}`);
                              toast.success("Type deleted");
                              fetchProductTypes();
                            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                          }}
                          className="text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-type-${pt.name}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
              {productTypes.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No types yet.</p>}
            </div>

            {/* Add new type */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center gap-2">
                <input
                  value={newTypeName}
                  onChange={e => setNewTypeName(e.target.value)}
                  placeholder="New type name..."
                  className="flex-1 text-xs px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#2EB4AC]"
                  data-testid="new-type-name-input"
                />
                <select
                  value={newTypeColor}
                  onChange={e => setNewTypeColor(e.target.value)}
                  className="text-[10px] px-2 py-2 border border-gray-200 rounded-lg bg-white"
                  data-testid="new-type-color-select"
                >
                  {TYPE_COLORS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <Button
                  size="sm"
                  className="btn-accent text-xs gap-1"
                  disabled={!newTypeName.trim()}
                  onClick={async () => {
                    if (!newTypeName.trim()) return;
                    try {
                      await axios.post(`${API}/product-types`, { name: newTypeName.trim(), color: newTypeColor });
                      toast.success("Type added");
                      setNewTypeName("");
                      fetchProductTypes();
                    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="add-type-btn"
                >
                  <Plus className="w-3 h-3" /> Add
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── PLATFORMS TAB ───
const COLOR_OPTIONS = [
  { bg: "bg-orange-100", text: "text-orange-600", label: "Orange" },
  { bg: "bg-blue-100", text: "text-blue-600", label: "Blue" },
  { bg: "bg-yellow-100", text: "text-yellow-600", label: "Yellow" },
  { bg: "bg-purple-100", text: "text-purple-600", label: "Purple" },
  { bg: "bg-red-100", text: "text-red-600", label: "Red" },
  { bg: "bg-green-100", text: "text-green-600", label: "Green" },
  { bg: "bg-pink-100", text: "text-pink-600", label: "Pink" },
  { bg: "bg-teal-100", text: "text-teal-600", label: "Teal" },
];

const PlatformsTab = ({ platforms, onPlatformsChange }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", icon_url: "",
    color_bg: "bg-gray-100", color_text: "text-gray-600", active: false, beta: false,
  });

  const openAdd = () => {
    setEditingPlatform(null);
    setForm({ name: "", description: "", icon_url: "", color_bg: "bg-gray-100", color_text: "text-gray-600", active: false, beta: false });
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingPlatform(p);
    setForm({
      name: p.name || "",
      description: p.description || "",
      icon_url: p.icon_url || "",
      color_bg: p.color_bg || "bg-gray-100",
      color_text: p.color_text || "text-gray-600",
      active: p.active || false,
      beta: p.beta || false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Platform name is required");
    setSaving(true);
    try {
      if (editingPlatform) {
        await axios.put(`${API}/platforms/${editingPlatform.platform_id}`, form);
        toast.success("Platform updated");
      } else {
        await axios.post(`${API}/platforms`, form);
        toast.success("Platform created");
      }
      setModalOpen(false);
      onPlatformsChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save platform");
    }
    setSaving(false);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/platforms/${p.platform_id}`);
      toast.success("Platform deleted");
      onPlatformsChange();
    } catch { toast.error("Failed to delete platform"); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/platforms/${p.platform_id}`, { active: !p.active });
      toast.success(`${p.name} ${!p.active ? "activated" : "deactivated"}`);
      onPlatformsChange();
    } catch { toast.error("Failed to toggle platform"); }
  };

  return (
    <>
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6" data-testid="platforms-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Platforms</h2>
            <p className="text-sm text-gray-500 mt-0.5">Manage marketplace platforms for scraping and tracking</p>
          </div>
          <Button size="sm" onClick={openAdd} className="btn-accent text-sm gap-1.5 rounded-lg" data-testid="add-platform-btn">
            <Plus className="w-4 h-4" /> Add Platform
          </Button>
        </div>

        <div className="space-y-3" data-testid="platforms-list">
          {platforms.map(p => (
            <div key={p.platform_id} className="flex items-center justify-between px-5 py-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors" data-testid={`platform-row-${p.platform_id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${p.color_bg || 'bg-gray-100'}`}>
                  {p.icon_url ? (
                    <img src={p.icon_url} alt="" className="w-5 h-5 object-contain" onError={e => e.target.style.display = 'none'} />
                  ) : (
                    <Globe className={`w-4 h-4 ${p.color_text || 'text-gray-500'}`} />
                  )}
                </div>
                <div>
                  <span className="font-semibold text-gray-900 text-sm">{p.name}</span>
                  {p.description && <p className="text-xs text-gray-400">{p.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => toggleActive(p)} className="inline-flex" data-testid={`toggle-platform-${p.platform_id}`}>
                  {p.active ? (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-700">Active</span>
                  ) : (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                  )}
                </button>
                {p.beta && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                    </span>
                    <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Beta</span>
                  </span>
                )}
                <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-testid={`edit-platform-${p.platform_id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-platform-${p.platform_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {platforms.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">No platforms configured.</div>
          )}
        </div>
      </div>

      {/* Platform Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPlatform ? "Edit Platform" : "Add Platform"}</DialogTitle>
            <DialogDescription>Configure platform details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Platform Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Meesho" className="text-sm" data-testid="platform-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Description</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Social commerce marketplace" className="text-sm" data-testid="platform-desc-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Icon URL</label>
              <div className="flex gap-2 items-center">
                <Input value={form.icon_url} onChange={e => setForm(f => ({ ...f, icon_url: e.target.value }))} placeholder="https://..." className="text-sm flex-1" data-testid="platform-icon-input" />
                {form.icon_url && <img src={form.icon_url} alt="preview" className="w-8 h-8 rounded object-contain border border-gray-200" onError={e => e.target.style.display = 'none'} />}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Color Theme</label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_OPTIONS.map(co => (
                  <button
                    key={co.bg}
                    onClick={() => setForm(f => ({ ...f, color_bg: co.bg, color_text: co.text }))}
                    className={`w-8 h-8 rounded-lg ${co.bg} border-2 transition-all ${form.color_bg === co.bg ? 'border-gray-900 scale-110' : 'border-transparent hover:border-gray-300'}`}
                    title={co.label}
                    data-testid={`color-${co.label.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Active</label>
              <button
                onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                className="flex items-center gap-1.5 text-sm"
                data-testid="platform-active-toggle"
              >
                {form.active ? (
                  <><ToggleRight className="w-6 h-6 text-[#2EB4AC]" /> <span className="text-[#2EB4AC] text-xs font-medium">Active</span></>
                ) : (
                  <><ToggleLeft className="w-6 h-6 text-gray-400" /> <span className="text-gray-400 text-xs font-medium">Inactive</span></>
                )}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Beta Tag</label>
              <button
                onClick={() => setForm(f => ({ ...f, beta: !f.beta }))}
                className="flex items-center gap-1.5 text-sm"
                data-testid="platform-beta-toggle"
              >
                {form.beta ? (
                  <><ToggleRight className="w-6 h-6 text-[#2EB4AC]" /> <span className="text-[#2EB4AC] text-xs font-medium">Beta</span></>
                ) : (
                  <><ToggleLeft className="w-6 h-6 text-gray-400" /> <span className="text-gray-400 text-xs font-medium">Off</span></>
                )}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)} className="text-gray-500">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="save-platform-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── PERMISSION CATALOG (loaded dynamically from backend) ───
// Local fallback if catalog fetch fails
const PERMISSION_CATALOG_FALLBACK = {
  groups: [
    { id: "scraping", label: "Scraping (per platform)", permissions: [
      { id: "scrape_amazon", label: "Amazon Delivery Tracker" },
      { id: "scrape_amazon_buybox", label: "Amazon Buy Box Monitor" },
      { id: "scrape_flipkart", label: "Flipkart" },
      { id: "scrape_blinkit", label: "Blinkit" },
      { id: "scrape_offline", label: "Offline (Google Maps)" },
      { id: "scrape_price_governance", label: "Price Governance" },
      { id: "scrape_analytics", label: "Delivery Analytics tab" },
    ]},
    { id: "scheduling", label: "Scheduling", permissions: [
      { id: "schedule_buybox", label: "Buy Box schedules" },
      { id: "schedule_delivery", label: "Delivery Tracker schedules" },
    ]},
    { id: "data", label: "Data & Visibility", permissions: [
      { id: "export_csv", label: "CSV / Excel export" },
      { id: "view_all_jobs", label: "View all users' jobs" },
      { id: "view_bandwidth", label: "Proxy bandwidth ticker" },
    ]},
    { id: "control_panel", label: "Control Panel", permissions: [
      { id: "cp_brands", label: "Manage Brands" },
      { id: "cp_products", label: "Manage Products & Pincodes" },
      { id: "cp_product_types", label: "Manage Product Types" },
      { id: "cp_platforms", label: "Manage Platforms" },
      { id: "cp_jobs", label: "View All-Jobs admin tab" },
      { id: "cp_users", label: "Manage Users & permissions" },
    ]},
  ],
  all_permissions: [
    "scrape_amazon","scrape_amazon_buybox","scrape_flipkart","scrape_blinkit",
    "scrape_offline","scrape_price_governance","scrape_analytics",
    "schedule_buybox","schedule_delivery",
    "export_csv","view_all_jobs","view_bandwidth",
    "cp_brands","cp_products","cp_product_types","cp_platforms","cp_jobs","cp_users",
  ],
  default_permissions: [
    "scrape_amazon","scrape_amazon_buybox","scrape_flipkart","scrape_blinkit",
    "scrape_offline","scrape_price_governance","scrape_analytics",
    "schedule_buybox","schedule_delivery","export_csv","view_bandwidth",
  ],
};

const groupColor = (gid) => ({
  scraping: 'bg-blue-100 text-blue-700 border-blue-200',
  scheduling: 'bg-amber-100 text-amber-700 border-amber-200',
  data: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  control_panel: 'bg-purple-100 text-purple-700 border-purple-200',
}[gid] || 'bg-gray-100 text-gray-700 border-gray-200');

// Render grouped permission toggles. `lockedIds` = perms that cannot be toggled (e.g. cp_users for super admin).
const PermissionGrid = ({ catalog, selected, setSelected, lockedIds = [], compact = false, testIdPrefix = "perm" }) => {
  const togglePerm = (pid) => {
    if (lockedIds.includes(pid)) return;
    setSelected(selected.includes(pid) ? selected.filter(x => x !== pid) : [...selected, pid]);
  };
  const toggleGroup = (group, allOn) => {
    const groupIds = group.permissions.map(p => p.id);
    if (allOn) {
      setSelected(selected.filter(p => !groupIds.includes(p) || lockedIds.includes(p)));
    } else {
      setSelected([...new Set([...selected, ...groupIds])]);
    }
  };
  return (
    <div className="space-y-4">
      {catalog.groups.map(g => {
        const allOn = g.permissions.every(p => selected.includes(p.id));
        const someOn = g.permissions.some(p => selected.includes(p.id));
        return (
          <div key={g.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <div className={`px-4 py-2 flex items-center justify-between border-b ${groupColor(g.id)} border-opacity-30`}>
              <span className="text-[12px] font-bold uppercase tracking-wide">{g.label}</span>
              <button
                type="button"
                onClick={() => toggleGroup(g, allOn)}
                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded hover:bg-white/40 transition-colors"
                data-testid={`${testIdPrefix}-group-toggle-${g.id}`}
              >
                {allOn ? 'Disable all' : someOn ? 'Enable all' : 'Enable all'}
              </button>
            </div>
            <div className={compact ? "divide-y divide-gray-100" : "p-2 grid grid-cols-1 sm:grid-cols-2 gap-2"}>
              {g.permissions.map(p => {
                const isOn = selected.includes(p.id);
                const isLocked = lockedIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between ${compact ? 'px-4 py-2' : 'px-3 py-2 rounded-lg border'} ${
                      isOn ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
                    } ${isLocked ? 'opacity-60' : ''}`}
                  >
                    <span className={`text-[12.5px] ${isOn ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>{p.label}</span>
                    <button
                      type="button"
                      onClick={() => togglePerm(p.id)}
                      disabled={isLocked}
                      className={isLocked ? 'cursor-not-allowed' : ''}
                      data-testid={`${testIdPrefix}-toggle-${p.id}`}
                      title={isLocked ? 'Locked' : ''}
                    >
                      {isOn ? <ToggleRight className="w-6 h-6 text-[#2EB4AC]" /> : <ToggleLeft className="w-6 h-6 text-gray-300" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── JOBS GROUPED BY USER ───
const StatusBadge = ({ status }) => {
  if (status === "processing") return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#2EB4AC] text-white inline-flex items-center gap-1">
      <Loader2 className="w-3 h-3 animate-spin" /> Running
    </span>
  );
  if (status === "completed") return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Completed</span>
  );
  if (status === "failed") return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">Failed</span>
  );
  return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{status}</span>;
};

const JobsByUser = ({ jobs, formatTime }) => {
  const [expanded, setExpanded] = useState({});

  // Group jobs by user
  const grouped = {};
  jobs.forEach(j => {
    const key = j.user_email || j.user_id || "unknown";
    if (!grouped[key]) grouped[key] = { name: j.user_name || key, email: key, jobs: [] };
    grouped[key].jobs.push(j);
  });
  const userGroups = Object.values(grouped);

  const toggleUser = (email) => setExpanded(prev => ({ ...prev, [email]: !prev[email] }));

  const handleExportJob = async (job) => {
    try {
      // The list endpoint strips `results` for performance; fetch the full job before export.
      let full = job;
      if (!job.results || job.results.length === 0) {
        const r = await axios.get(`${API}/admin/jobs/${job.job_id}`);
        full = r.data;
      }
      const results = full?.results || [];
      if (!results.length) {
        toast.error("This job has no results to export");
        return;
      }
      const headers = Object.keys(results[0]);
      const escape = (v) => {
        if (v === null || v === undefined) return '';
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [headers.join(","), ...results.map(r => headers.map(h => escape(r[h])).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `horizon_${full.platform || 'job'}_${full.job_id}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Exported ${results.length} row${results.length !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  if (jobs.length === 0) return <div className="text-center py-8 text-gray-400 text-sm">No jobs found.</div>;

  return (
    <div className="space-y-2" data-testid="jobs-by-user">
      {userGroups.map(group => {
        const isOpen = expanded[group.email];
        const running = group.jobs.filter(j => j.status === "processing").length;
        const completed = group.jobs.filter(j => j.status === "completed").length;
        const failed = group.jobs.filter(j => j.status === "failed").length;
        return (
          <div key={group.email} className="rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleUser(group.email)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors bg-white"
              data-testid={`jobs-group-${group.email}`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="font-semibold text-sm text-gray-900">{group.name}</span>
                <span className="text-xs text-gray-400">({group.email})</span>
              </div>
              <div className="flex items-center gap-2">
                {running > 0 && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#2EB4AC] text-white">{running} running</span>}
                {completed > 0 && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">{completed} done</span>}
                {failed > 0 && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">{failed} failed</span>}
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{group.jobs.length} total</span>
              </div>
            </button>
            {isOpen && (
              <div className="px-4 pb-3 space-y-1.5 bg-gray-50/50">
                {group.jobs.map(job => {
                  const total = job.total || 0;
                  const done = job.completed_count || 0;
                  const successCount = job.progress_message?.match(/(\d+)\/\d+ successful/)?.[1] || "-";
                  return (
                    <div
                      key={job.job_id}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
                        job.status === "processing" ? 'border-[#2EB4AC] bg-[#F0FDFA]'
                        : job.status === "failed" ? 'border-red-200 bg-red-50/50'
                        : 'border-gray-100 bg-white'
                      }`}
                      data-testid={`admin-job-${job.job_id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {job.platform === 'offline' 
                                ? `${(job.keywords || []).length || (job.keyword ? 1 : 0)} Keywords` 
                                : `${(job.asins || []).length} ${job.platform === 'flipkart' ? 'FSNs' : 'ASINs'}`} x {(job.pincodes || []).length} Pins
                            </span>
                            <StatusBadge status={job.status} />
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400">{formatTime(job.created_at)}</span>
                            {job.progress_message && (
                              <>
                                <span className="text-[10px] text-gray-300">|</span>
                                <span className="text-[10px] text-gray-500">{job.progress_message}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(job.status === 'completed' || (job.results?.length > 0)) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleExportJob(job); }}
                            className="inline-flex items-center gap-1 text-[10px] text-[#32B4AC] hover:text-[#2a9d96] hover:bg-[#F0FDFA] px-2 py-1 rounded transition-colors"
                            data-testid={`export-job-${job.job_id}`}
                          >
                            <Download className="w-3 h-3" /> CSV
                          </button>
                        )}
                        <div className="text-right text-xs text-gray-500 min-w-[50px]">
                          {done}/{total}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── USERS TAB (admin only) ───
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [catalog, setCatalog] = useState(PERMISSION_CATALOG_FALLBACK);
  const [invitePerms, setInvitePerms] = useState(PERMISSION_CATALOG_FALLBACK.default_permissions);
  const [allJobs, setAllJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [showJobs, setShowJobs] = useState(false);

  useEffect(() => {
    axios.get(`${API}/permissions/catalog`).then(r => {
      setCatalog(r.data);
      setInvitePerms(r.data.default_permissions || []);
    }).catch(() => {});
  }, []);

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/users`);
      setUsers(r.data || []);
    } catch { toast.error("Failed to load users"); }
    setLoading(false);
  };

  const fetchAllJobs = async () => {
    setJobsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/jobs?limit=50`);
      setAllJobs(r.data || []);
    } catch { toast.error("Failed to load jobs"); }
    setJobsLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const openEdit = (u) => {
    setEditingUser(u);
    setEditPerms([...u.permissions]);
  };

  const togglePerm = (perm, permsList, setter) => {
    setter(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/users/${encodeURIComponent(editingUser.email)}/permissions`, { permissions: editPerms });
      toast.success(`Permissions updated for ${editingUser.email}`);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  const handleReset = async (email) => {
    if (!window.confirm(`Reset ${email} to default permissions?`)) return;
    try {
      await axios.delete(`${API}/users/${encodeURIComponent(email)}/permissions`);
      toast.success("Permissions reset to default");
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset");
    }
  };

  const handleBanToggle = async (u) => {
    const banning = !u.banned;
    if (!window.confirm(banning ? `Ban ${u.email}? They will be signed out immediately and cannot log in.` : `Unban ${u.email}? They will be able to log in again.`)) return;
    try {
      await axios.put(`${API}/users/${encodeURIComponent(u.email)}/ban`, { banned: banning });
      toast.success(banning ? `${u.email} banned` : `${u.email} unbanned`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update ban");
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return toast.error("Email required");
    try {
      await axios.post(`${API}/users/invite`, { email: inviteEmail.trim().toLowerCase(), permissions: invitePerms });
      toast.success(`Pre-configured permissions for ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail("");
      setInvitePerms(catalog.default_permissions || []);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2EB4AC]" /></div>;

  return (
    <>
      {/* User Management Card */}
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 mb-4" data-testid="users-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Permissions</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              All users have full access by default. Revoke or grant specific permissions as needed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => { setShowJobs(!showJobs); if (!showJobs && allJobs.length === 0) fetchAllJobs(); }}
              className="text-sm gap-1.5 rounded-lg"
              data-testid="toggle-all-jobs-btn"
            >
              <Briefcase className="w-4 h-4" /> {showJobs ? "Hide" : "All"} Jobs
            </Button>
            <Button size="sm" onClick={() => setInviteOpen(true)} className="btn-accent text-sm gap-1.5 rounded-lg" data-testid="invite-user-btn">
              <Plus className="w-4 h-4" /> Pre-configure User
            </Button>
          </div>
        </div>

        {/* Permission summary */}
        <div className="flex flex-wrap gap-2 mb-5 items-center text-[11px] text-gray-500">
          <span className="font-medium">{catalog.all_permissions.length} granular permissions across {catalog.groups.length} groups</span>
          {catalog.groups.map(g => (
            <span key={g.id} className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${groupColor(g.id)}`}>
              {g.label.replace(/\s*\(.+\)/, '')} · {g.permissions.length}
            </span>
          ))}
        </div>

        {/* User list */}
        <div className="space-y-3" data-testid="users-list">
          {users.map(u => (
            <div key={u.user_id} className={`flex items-center justify-between px-5 py-4 rounded-xl border transition-colors ${u.banned ? "border-rose-200 bg-rose-50/40" : "border-gray-200 bg-white hover:border-gray-300"}`} data-testid={`user-row-${u.email}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#E8F8F5] flex items-center justify-center text-[#2EB4AC] font-bold text-sm">
                    {(u.name || u.email)[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm truncate">{u.name || u.email}</span>
                    {u.is_super_admin && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase">Super Admin</span>
                    )}
                    {u.banned && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase" title={u.banned_by ? `Banned by ${u.banned_by}` : undefined} data-testid={`banned-badge-${u.email}`}>Banned</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{u.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Permission group summary chips */}
                <div className="flex gap-1 flex-wrap max-w-[280px] justify-end">
                  {catalog.groups.map(g => {
                    const groupIds = g.permissions.map(x => x.id);
                    const enabled = groupIds.filter(id => u.permissions.includes(id)).length;
                    const total = groupIds.length;
                    if (enabled === 0) return null;
                    const allOn = enabled === total;
                    return (
                      <span
                        key={g.id}
                        title={`${g.label}: ${enabled}/${total} enabled`}
                        className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${groupColor(g.id)} ${allOn ? '' : 'opacity-60'}`}
                      >
                        {g.label.replace(/\s*\(.+\)/, '').split(' ')[0]} {allOn ? '·' : `${enabled}/${total}`}
                      </span>
                    );
                  })}
                  {u.permissions.length === 0 && (
                    <span className="text-[10px] text-gray-400 italic">No permissions</span>
                  )}
                </div>
                {/* Activity info */}
                <div className="text-right min-w-[60px]">
                  <div className="text-[10px] text-gray-400 flex items-center gap-1 justify-end">
                    <Briefcase className="w-3 h-3" /> {u.total_jobs}
                  </div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1 justify-end">
                    <Clock className="w-3 h-3" /> {formatTime(u.last_active)}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-testid={`edit-user-${u.email}`}>
                    <UserCog className="w-4 h-4" />
                  </button>
                  {!u.is_super_admin && (
                    <button onClick={() => handleReset(u.email)} className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors" title="Reset to defaults" data-testid={`reset-user-${u.email}`}>
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  {!u.is_super_admin && (
                    <button onClick={() => handleBanToggle(u)} className={`p-1.5 rounded-lg transition-colors ${u.banned ? "text-rose-600 hover:text-emerald-600 hover:bg-emerald-50" : "text-gray-400 hover:text-rose-600 hover:bg-rose-50"}`} title={u.banned ? "Unban user" : "Ban user from login"} data-testid={`ban-toggle-user-${u.email}`}>
                      {u.banned ? <ShieldCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">No users have logged in yet.</div>
          )}
        </div>
      </div>

      {/* All Jobs (admin view) — grouped by user */}
      {showJobs && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6" data-testid="admin-jobs-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">All Scraping Jobs</h2>
              <p className="text-sm text-gray-500 mt-0.5">Jobs from all users, grouped by user</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAllJobs} className="text-xs gap-1.5 rounded-lg" data-testid="refresh-jobs-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Refresh
            </Button>
          </div>
          {jobsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#2EB4AC]" /></div>
          ) : (
            <JobsByUser jobs={allJobs} formatTime={formatTime} />
          )}
        </div>
      )}

      {/* Edit Permissions Modal */}
      <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Permissions</DialogTitle>
            <DialogDescription>{editingUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <PermissionGrid
              catalog={catalog}
              selected={editPerms}
              setSelected={setEditPerms}
              lockedIds={editingUser?.is_super_admin ? ["cp_users"] : []}
              testIdPrefix="edit"
            />
            <div className="mt-4 flex items-center justify-between text-[11px] text-gray-500">
              <span><span className="font-semibold text-gray-700">{editPerms.length}</span> of {catalog.all_permissions.length} permissions enabled</span>
              <button
                onClick={() => setEditPerms(catalog.default_permissions || [])}
                className="text-[#2EB4AC] hover:underline font-medium"
                data-testid="edit-reset-defaults"
              >Reset to defaults</button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingUser(null)} className="text-gray-500">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="save-permissions-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite / Pre-configure Modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pre-configure User</DialogTitle>
            <DialogDescription>Set permissions before the user logs in. They'll inherit these on first login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Email *</label>
              <Input
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="name@wellversed.in"
                className="text-sm"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Permissions</label>
                <button
                  onClick={() => setInvitePerms(catalog.default_permissions || [])}
                  className="text-[10px] font-medium text-[#2EB4AC] hover:underline"
                  data-testid="invite-reset-defaults"
                >Reset to defaults</button>
              </div>
              <PermissionGrid
                catalog={catalog}
                selected={invitePerms}
                setSelected={setInvitePerms}
                testIdPrefix="invite"
              />
              <div className="mt-3 text-[11px] text-gray-500">
                <span className="font-semibold text-gray-700">{invitePerms.length}</span> of {catalog.all_permissions.length} permissions enabled
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)} className="text-gray-500">Cancel</Button>
            <Button onClick={handleInvite} className="btn-accent" data-testid="confirm-invite-btn">
              <Plus className="w-4 h-4 mr-1" /> Pre-configure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── PINCODES TAB ───
// ─── Country picker (searchable combobox) ─────────────────────────────
// Lightweight button + popover menu with a search input. Filters both by
// country name and ISO code, so users can type "gb", "britain", or
// "united kingdom" and land on the same row. Keyboard-friendly:
// Enter picks the first match, Escape closes.
const CountryPicker = ({ countries, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const selected = countries.find((c) => c.code === value);

  useEffect(() => {
    if (!open) return;
    // Autofocus search once the menu opens
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? countries.filter((c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
      )
    : countries;

  const pick = (c) => { onChange(c.code); setOpen(false); setQuery(""); };

  return (
    <div className="relative inline-flex items-center gap-1.5" data-testid="cp-pincodes-country">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Country</span>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-800 bg-white border border-gray-200 rounded-md hover:border-[#2EB4AC] focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none cursor-pointer min-w-[180px] justify-between"
        data-testid="cp-pincodes-country-select"
      >
        <span className="truncate">{selected?.name || "Select country"}</span>
        <span className="text-[10px] text-gray-400">▾</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          className="absolute top-full right-0 mt-1 z-40 w-72 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden"
          data-testid="cp-pincodes-country-menu"
        >
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered[0]) pick(filtered[0]);
              }}
              placeholder="Search country or code…"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none"
              data-testid="cp-pincodes-country-search"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">No countries match "{query}"</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => pick(c)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-[#F0FDFA] transition-colors ${
                    c.code === value ? "bg-[#F0FDFA] text-[#2EB4AC] font-semibold" : "text-gray-700"
                  }`}
                  data-testid={`cp-pincodes-country-option-${c.code}`}
                >
                  <span className="text-[10px] font-mono text-gray-400 w-6 shrink-0">{c.code}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.code === value && <span className="text-[#2EB4AC] text-xs">✓</span>}
                </button>
              ))
            )}
          </div>
          <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500">
            {filtered.length} of {countries.length} countries
          </div>
        </div>
      )}
    </div>
  );
};

const PincodesTab = () => {
  const [pincodes, setPincodes] = useState([]);
  const [countries, setCountries] = useState([]);
  // Selected country persists in localStorage so users don't have to
  // re-pick every session. Defaults to India (backward-compatible).
  const [country, setCountry] = useState(() => {
    try { return localStorage.getItem("cp_pincodes_country") || "IN"; }
    catch { return "IN"; }
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedStates, setExpandedStates] = useState({});
  const [expandedCities, setExpandedCities] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ state: "", city: "", pincode: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const countryMeta = countries.find((c) => c.code === country) || { name: "India", postal_label: "Pincode", state_label: "State" };

  const fetchPincodes = async (cty = country) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pincodes`, { params: { country: cty } });
      setPincodes(r.data || []);
    } catch { toast.error("Failed to load postal codes"); }
    setLoading(false);
  };

  // Load supported countries once
  useEffect(() => {
    axios.get(`${API}/pincodes/countries`)
      .then((r) => setCountries(r.data || []))
      .catch(() => setCountries([{ code: "IN", name: "India", postal_label: "Pincode", state_label: "State" }]));
  }, []);
  // Reload data (and persist choice) when country changes
  useEffect(() => {
    try { localStorage.setItem("cp_pincodes_country", country); } catch {}
    fetchPincodes(country);
    // Collapse tree so users get a fresh view per country
    setExpandedStates({});
    setExpandedCities({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // Group pincodes by state -> city
  const grouped = {};
  pincodes.forEach(p => {
    if (!grouped[p.state]) grouped[p.state] = {};
    if (!grouped[p.state][p.city]) grouped[p.state][p.city] = [];
    grouped[p.state][p.city].push(p.pincode);
  });

  const filteredStates = Object.keys(grouped).filter(state => {
    if (!search) return true;
    const s = search.toLowerCase();
    if (state.toLowerCase().includes(s)) return true;
    return Object.keys(grouped[state]).some(city =>
      city.toLowerCase().includes(s) ||
      grouped[state][city].some(pc => pc.includes(s))
    );
  }).sort();

  const toggleState = (state) => setExpandedStates(prev => ({ ...prev, [state]: !prev[state] }));
  const toggleCity = (key) => setExpandedCities(prev => ({ ...prev, [key]: !prev[key] }));

  const handleAdd = async () => {
    if (!addForm.state.trim() || !addForm.city.trim() || !addForm.pincode.trim()) return toast.error("All fields required");
    try {
      await axios.post(`${API}/pincodes`, { ...addForm, country });
      toast.success(`${countryMeta.postal_label} ${addForm.pincode} added`);
      setAddForm({ state: "", city: "", pincode: "" });
      setAddOpen(false);
      fetchPincodes();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (pincode) => {
    if (!window.confirm(`Delete pincode ${pincode}?`)) return;
    try {
      await axios.delete(`${API}/pincodes/${encodeURIComponent(pincode)}`, { params: { country } });
      toast.success(`Deleted ${pincode}`);
      fetchPincodes();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteCity = async (state, city) => {
    if (!window.confirm(`Delete all pincodes in ${city}, ${state}?`)) return;
    try {
      await axios.delete(`${API}/pincodes/city/${encodeURIComponent(state)}/${encodeURIComponent(city)}`, { params: { country } });
      toast.success(`Deleted pincodes from ${city}`);
      fetchPincodes();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteState = async (state) => {
    if (!window.confirm(`Delete all pincodes in ${state}?`)) return;
    try {
      await axios.delete(`${API}/pincodes/state/${encodeURIComponent(state)}`, { params: { country } });
      toast.success(`Deleted pincodes from ${state}`);
      fetchPincodes();
    } catch { toast.error("Failed to delete"); }
  };

  const downloadTemplate = () => {
    // Hit the backend so the CSV rows match the selected country's format.
    // Content-Disposition takes care of filename; we just trigger the download.
    const a = document.createElement("a");
    a.href = `${API}/pincodes/template.csv?country=${encodeURIComponent(country)}`;
    a.rel = "noopener";
    a.download = `pincodes_template_${country}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      const lines = text.split("\n").filter(l => l.trim());
      if (lines.length < 2) return toast.error("CSV must have header + data rows");
      const header = lines[0].toLowerCase().split(",").map(h => h.trim());
      const stateIdx = header.indexOf("state");
      const cityIdx = header.indexOf("city");
      const pincodeIdx = header.indexOf("pincode");
      if (stateIdx === -1 || cityIdx === -1 || pincodeIdx === -1) {
        return toast.error("CSV must have State, City, Pincode columns");
      }
      const rows = lines.slice(1).map(l => {
        const cols = l.split(",").map(c => c.trim());
        return { state: cols[stateIdx] || "", city: cols[cityIdx] || "", pincode: cols[pincodeIdx] || "" };
      }).filter(r => r.state && r.city && r.pincode);
      setImportData(rows);
      setImportOpen(true);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleBulkImport = async () => {
    if (!importData.length) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/pincodes/bulk`, { rows: importData, default_country: country }, { timeout: 300000 });
      toast.success(res.data.message);
      setImportOpen(false);
      setImportData([]);
      fetchPincodes();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2EB4AC]" /></div>;

  const totalPincodes = pincodes.length;
  const totalStates = Object.keys(grouped).length;
  const totalCities = Object.values(grouped).reduce((acc, cities) => acc + Object.keys(cities).length, 0);

  return (
    <>
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 mb-4" data-testid="pincodes-card">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{countryMeta.postal_label} Management</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {totalPincodes} {countryMeta.postal_label.toLowerCase()}s across {totalCities} cities in {totalStates} {countryMeta.state_label.toLowerCase()}s
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Country picker — searchable combobox, scopes the whole tab. */}
            <CountryPicker
              countries={countries}
              value={country}
              onChange={setCountry}
            />
            <Button size="sm" onClick={() => setAddOpen(true)} className="btn-accent text-sm gap-1.5 rounded-lg" data-testid="add-pincode-btn">
              <Plus className="w-4 h-4" /> Add {countryMeta.postal_label}
            </Button>
          </div>
        </div>

        {/* Bulk Upload Card */}
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-5 mb-5" data-testid="bulk-upload-pincodes-card">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4 text-[#2EB4AC]" />
            <span className="font-semibold text-sm text-gray-900">Bulk Import Pincodes</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">Upload a CSV with State, City, Pincode columns to bulk-add pincodes.</p>
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors" data-testid="download-pincode-template">
              <Download className="w-4 h-4" /> Download Template
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv" className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#32B4AC] rounded-lg hover:bg-[#2a9d96] transition-colors" data-testid="upload-pincodes-btn">
              <Upload className="w-4 h-4" /> Upload CSV
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search state, city, or pincode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="pincode-search"
          />
        </div>

        {/* Grouped display */}
        {filteredStates.length > 0 ? (
          <div className="space-y-2" data-testid="pincodes-list">
            {filteredStates.map(state => (
              <div key={state} className="rounded-lg border border-gray-200 overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleState(state)}
                >
                  <div className="flex items-center gap-2">
                    {expandedStates[state] ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    <MapPin className="w-4 h-4 text-[#32B4AC]" />
                    <span className="font-medium text-sm text-gray-900">{state}</span>
                    <span className="text-xs text-gray-400">
                      {Object.keys(grouped[state]).length} cities, {Object.values(grouped[state]).reduce((a, p) => a + p.length, 0)} pincodes
                    </span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteState(state); }}
                    className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`delete-state-${state}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {expandedStates[state] && (
                  <div className="px-4 py-2 space-y-1">
                    {Object.keys(grouped[state]).sort().map(city => {
                      const cityKey = `${state}-${city}`;
                      const cityPincodes = grouped[state][city];
                      return (
                        <div key={cityKey} className="ml-4">
                          <div
                            className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 rounded px-2 transition-colors"
                            onClick={() => toggleCity(cityKey)}
                          >
                            <div className="flex items-center gap-2">
                              {expandedCities[cityKey] ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                              <span className="text-sm text-gray-700">{city}</span>
                              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{cityPincodes.length}</span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteCity(state, city); }}
                              className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          {expandedCities[cityKey] && (
                            <div className="ml-8 flex flex-wrap gap-1.5 py-1.5">
                              {cityPincodes.sort().map(pc => (
                                <span key={pc} className="inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded-md group hover:bg-red-50 transition-colors">
                                  {pc}
                                  <button onClick={() => handleDelete(pc)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                                    <Trash2 className="w-2.5 h-2.5" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400 text-sm">
            {search ? "No matching pincodes found" : "No pincodes yet. Upload a CSV or add manually."}
          </div>
        )}
      </div>

      {/* Add Pincode Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Pincode</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="State (e.g., Maharashtra)" value={addForm.state} onChange={(e) => setAddForm(f => ({ ...f, state: e.target.value }))} data-testid="add-pincode-state" />
            <Input placeholder="City (e.g., Mumbai)" value={addForm.city} onChange={(e) => setAddForm(f => ({ ...f, city: e.target.value }))} data-testid="add-pincode-city" />
            <Input placeholder="Pincode (e.g., 400001)" value={addForm.pincode} onChange={(e) => setAddForm(f => ({ ...f, pincode: e.target.value }))} data-testid="add-pincode-value" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="btn-accent" data-testid="confirm-add-pincode">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Pincodes from CSV</DialogTitle>
            <DialogDescription>
              Found {importData.length} pincodes. Duplicates will be updated with new state/city values.
            </DialogDescription>
          </DialogHeader>
          {importData.length > 0 && (
            <div className="max-h-[300px] overflow-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>State</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Pincode</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importData.slice(0, 50).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{row.state}</TableCell>
                      <TableCell className="text-xs">{row.city}</TableCell>
                      <TableCell className="text-xs font-mono">{row.pincode}</TableCell>
                    </TableRow>
                  ))}
                  {importData.length > 50 && (
                    <TableRow><TableCell colSpan={3} className="text-center text-xs text-gray-400">...and {importData.length - 50} more</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportData([]); }}>Cancel</Button>
            <Button onClick={handleBulkImport} disabled={importing} className="btn-accent" data-testid="confirm-import-pincodes">
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Import {importData.length} Pincodes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── KEYWORDS TAB (Offline Maps keyword library) ───
const KeywordsTab = () => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addForm, setAddForm] = useState({ keyword: "", category: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ keyword: "", category: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [collapsedCats, setCollapsedCats] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/offline-keywords`);
      const data = r.data || [];
      setKeywords(data);
      // Collapse all categories by default — easier to scan the library.
      const cats = new Set(data.map(k => k.category || "Uncategorized"));
      setCollapsedCats(cats);
    } catch {
      toast.error("Failed to load keywords");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const filtered = keywords.filter(k => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (k.keyword || "").toLowerCase().includes(s) || (k.category || "").toLowerCase().includes(s);
  });
  const grouped = {};
  filtered.forEach(k => {
    const c = k.category || "Uncategorized";
    if (!grouped[c]) grouped[c] = [];
    grouped[c].push(k);
  });
  const categories = Object.keys(grouped).sort();

  const handleAdd = async () => {
    const kw = addForm.keyword.trim();
    if (!kw) return toast.error("Keyword required");
    try {
      await axios.post(`${API}/offline-keywords`, {
        keyword: kw,
        category: addForm.category.trim() || "Uncategorized",
      });
      toast.success(`Added "${kw}"`);
      setAddForm({ keyword: "", category: addForm.category });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    }
  };

  const handleSaveEdit = async (id) => {
    const kw = editForm.keyword.trim();
    if (!kw) return toast.error("Keyword required");
    try {
      await axios.put(`${API}/offline-keywords/${id}`, {
        keyword: kw,
        category: editForm.category.trim() || "Uncategorized",
      });
      toast.success("Updated");
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async (k) => {
    if (!window.confirm(`Delete keyword "${k.keyword}"?`)) return;
    try {
      await axios.delete(`${API}/offline-keywords/${k.id}`);
      toast.success(`Deleted "${k.keyword}"`);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(k.id); return n; });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected keyword(s)?`)) return;
    try {
      const r = await axios.post(`${API}/offline-keywords/bulk-delete`, { ids: Array.from(selectedIds) });
      toast.success(r.data?.message || "Deleted");
      setSelectedIds(new Set());
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to bulk delete");
    }
  };

  const handleBulkImport = async () => {
    const text = bulkText.trim();
    if (!text) return toast.error("Paste at least one keyword");
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/offline-keywords/bulk`, {
        text,
        category: bulkCategory.trim() || "Uncategorized",
      });
      toast.success(r.data?.message || "Imported");
      setBulkText("");
      setBulkOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadSampleCSV = () => {
    const csv = "keyword,category\nvape store,Wellness\nprotein shop,Wellness\npharmacy,Health\nmedical store,Health\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "keywords_sample.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      // Detect headered CSV (`keyword,category`) vs plain-line list.
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (!lines.length) { toast.error("File is empty"); return; }
      const header = lines[0].toLowerCase();
      const hasHeader = header.includes("keyword") && header.includes(",");
      let parsedRows;
      if (hasHeader) {
        const cols = lines[0].split(",").map(c => c.trim().toLowerCase());
        const kwIdx = cols.indexOf("keyword");
        const catIdx = cols.indexOf("category");
        parsedRows = lines.slice(1).map(ln => {
          const parts = ln.split(",").map(s => s.trim());
          return { keyword: parts[kwIdx] || "", category: catIdx >= 0 ? (parts[catIdx] || "") : "" };
        }).filter(r => r.keyword);
      } else {
        // Plain list — one keyword per line, no headers. Use category from dialog field.
        parsedRows = lines.map(kw => ({ keyword: kw, category: "" }));
      }
      if (!parsedRows.length) { toast.error("No keywords found in file"); return; }
      // If the file has its own categories (header mode), send rows directly so each
      // row keeps its category. Otherwise fall back to textarea-prefill flow.
      if (hasHeader && parsedRows.some(r => r.category)) {
        setBulkBusy(true);
        try {
          const r = await axios.post(`${API}/offline-keywords/bulk`, { rows: parsedRows });
          toast.success(r.data?.message || "Imported");
          setBulkText("");
          setBulkOpen(false);
          fetchAll();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Bulk import failed");
        } finally {
          setBulkBusy(false);
        }
      } else {
        setBulkText(parsedRows.map(r => r.keyword).join("\n"));
        toast.success(`Loaded ${parsedRows.length} keyword${parsedRows.length !== 1 ? "s" : ""} — set category & import`);
      }
    } catch (err) {
      toast.error("Failed to read file");
    } finally {
      // Reset the input so re-uploading the same file works
      e.target.value = "";
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleCategorySelect = (cat) => {
    const ids = (grouped[cat] || []).map(k => k.id);
    const allSelected = ids.every(i => selectedIds.has(i));
    setSelectedIds(prev => {
      const n = new Set(prev);
      ids.forEach(i => { if (allSelected) n.delete(i); else n.add(i); });
      return n;
    });
  };
  const toggleCollapse = (cat) => {
    setCollapsedCats(prev => {
      const n = new Set(prev);
      if (n.has(cat)) n.delete(cat); else n.add(cat);
      return n;
    });
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="keywords-tab">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Offline Keywords Library</h2>
            <p className="text-xs text-gray-500 mt-0.5">Reusable search terms for the Offline (Google Maps) scraper.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="h-9 pl-7 text-xs w-48"
                data-testid="keywords-search-input"
              />
            </div>
            {selectedIds.size > 0 && (
              <Button
                variant="outline"
                className="h-9 text-xs border-red-200 text-red-600 hover:bg-red-50"
                onClick={handleBulkDelete}
                data-testid="keywords-bulk-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Delete {selectedIds.size}
              </Button>
            )}
            <Button
              variant="outline"
              className="h-9 text-xs"
              onClick={() => setBulkOpen(true)}
              data-testid="keywords-bulk-import-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              Bulk Import
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Input
            value={addForm.keyword}
            onChange={(e) => setAddForm({ ...addForm, keyword: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="New keyword (e.g. vape store)"
            className="h-9 text-xs flex-1"
            data-testid="keyword-add-input"
          />
          <Input
            value={addForm.category}
            onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="Category (optional)"
            className="h-9 text-xs w-40"
            data-testid="keyword-add-category"
          />
          <Button
            className="h-9 text-xs bg-[#2EB4AC] hover:bg-[#268f88]"
            onClick={handleAdd}
            data-testid="keyword-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-sm text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : keywords.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">
            No keywords yet. Add one above or use Bulk Import.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-400">No matches for "{search}"</div>
        ) : (
          <div className="space-y-2" data-testid="keywords-list">
            {categories.map(cat => {
              const items = grouped[cat];
              const collapsed = collapsedCats.has(cat);
              const ids = items.map(k => k.id);
              const allSelected = ids.every(i => selectedIds.has(i));
              const someSelected = !allSelected && ids.some(i => selectedIds.has(i));
              const { icon: CatIcon, tone } = getCategoryIcon(cat);
              return (
                <div key={cat} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={() => toggleCategorySelect(cat)}
                      className="h-3.5 w-3.5 accent-[#2EB4AC] cursor-pointer"
                      data-testid={`keywords-cat-select-${cat}`}
                    />
                    <button
                      onClick={() => toggleCollapse(cat)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                      data-testid={`keywords-cat-toggle-${cat}`}
                    >
                      {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md border ${tone}`}>
                        <CatIcon className="w-3.5 h-3.5" />
                      </span>
                      <span>{cat}</span>
                      <span className="text-[10px] font-normal text-gray-400">({items.length})</span>
                    </button>
                  </div>
                  {!collapsed && (
                    <Table>
                      <TableBody>
                        {items.map(k => {
                          const isEditing = editingId === k.id;
                          const isSel = selectedIds.has(k.id);
                          return (
                            <TableRow key={k.id} data-testid={`keyword-row-${k.id}`}>
                              <TableCell className="w-8 py-2">
                                <input
                                  type="checkbox"
                                  checked={isSel}
                                  onChange={() => toggleSelect(k.id)}
                                  className="h-3.5 w-3.5 accent-[#2EB4AC] cursor-pointer"
                                  data-testid={`keyword-checkbox-${k.id}`}
                                />
                              </TableCell>
                              <TableCell className="py-2">
                                {isEditing ? (
                                  <Input
                                    value={editForm.keyword}
                                    onChange={(e) => setEditForm({ ...editForm, keyword: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(k.id); if (e.key === "Escape") setEditingId(null); }}
                                    className="h-7 text-xs"
                                    autoFocus
                                    data-testid={`keyword-edit-input-${k.id}`}
                                  />
                                ) : (
                                  <span className="text-sm text-gray-800">{k.keyword}</span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 w-40">
                                {isEditing ? (
                                  <Input
                                    value={editForm.category}
                                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                    className="h-7 text-xs"
                                  />
                                ) : (() => {
                                  const { icon: RowIcon, tone } = getCategoryIcon(k.category);
                                  return (
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] uppercase tracking-wide ${tone}`}>
                                      <RowIcon className="w-3 h-3" />
                                      <span>{k.category}</span>
                                    </span>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="py-2 w-24 text-right">
                                {isEditing ? (
                                  <div className="flex gap-1 justify-end">
                                    <Button
                                      size="sm"
                                      className="h-7 px-2 text-[10px] bg-[#2EB4AC] hover:bg-[#268f88]"
                                      onClick={() => handleSaveEdit(k.id)}
                                      data-testid={`keyword-save-${k.id}`}
                                    >Save</Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-[10px]"
                                      onClick={() => setEditingId(null)}
                                    >Cancel</Button>
                                  </div>
                                ) : (
                                  <div className="flex gap-1 justify-end">
                                    <button
                                      onClick={() => { setEditingId(k.id); setEditForm({ keyword: k.keyword, category: k.category }); }}
                                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                                      data-testid={`keyword-edit-btn-${k.id}`}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(k)}
                                      className="p-1.5 rounded hover:bg-red-50 text-red-500"
                                      data-testid={`keyword-delete-btn-${k.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="keywords-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Keywords</DialogTitle>
            <DialogDescription>Upload a CSV (with <code className="text-[10px] bg-gray-100 px-1 rounded">keyword,category</code> headers) or paste one keyword per line.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2">
              <button
                onClick={downloadSampleCSV}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
                data-testid="keywords-bulk-sample-btn"
              >
                <Download className="w-3 h-3" /> Sample CSV
              </button>
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
                id="keywords-bulk-file-input"
                data-testid="keywords-bulk-file-input"
              />
              <label
                htmlFor="keywords-bulk-file-input"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-white bg-[#2EB4AC] rounded-md hover:bg-[#268f88] transition-colors cursor-pointer"
                data-testid="keywords-bulk-upload-btn"
              >
                <Upload className="w-3 h-3" /> Upload CSV / TXT
              </label>
              <span className="text-[10px] text-gray-400 ml-auto">CSV preserves per-row categories</span>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Category (applies to pasted rows)</label>
              <Input
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                placeholder="e.g. Wellness, Health (defaults to Uncategorized)"
                className="h-9 text-xs mt-1"
                data-testid="keywords-bulk-category"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Keywords</label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"vape store\nprotein shop\nfitness store"}
                rows={9}
                className="mt-1 w-full text-xs font-mono px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]"
                data-testid="keywords-bulk-textarea"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                {bulkText.split("\n").filter(l => l.trim()).length} keyword(s) ready
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button
              className="bg-[#2EB4AC] hover:bg-[#268f88]"
              onClick={handleBulkImport}
              disabled={bulkBusy || !bulkText.trim()}
              data-testid="keywords-bulk-submit"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── MAIN CONTROL PANEL ───
// ────────────────────────────────────────────────────────────────────
// Standardization Tab — central hub for all attribute aliases
// (raw → display mappings) used in Market Research analytics.
// Features: searchable/filterable table, inline edit + delete, CSV export,
// bulk import (paste / CSV).
// ────────────────────────────────────────────────────────────────────
const ATTR_OPTIONS = [
  { value: "",          label: "All attributes" },
  { value: "brand",     label: "Brand" },
  { value: "flavour",   label: "Flavour" },
  { value: "item_form", label: "Item Form" },
  { value: "diet_type", label: "Diet Type" },
  { value: "specialty", label: "Specialty" },
  { value: "pack_size", label: "Pack Size" },
];
const ATTR_LABEL = ATTR_OPTIONS.reduce((m, o) => { if (o.value) m[o.value] = o.label; return m; }, {});

const _canonicalAttr = (s) => {
  const norm = (s || "").trim().toLowerCase();
  const map = {
    "brand": "brand",
    "flavour": "flavour", "flavor": "flavour",
    "item form": "item_form", "item_form": "item_form",
    "diet type": "diet_type", "diet_type": "diet_type",
    "specialty": "specialty",
    "pack size": "pack_size", "pack_size": "pack_size",
  };
  if (map[norm]) return map[norm];
  return norm.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
};

// ─── Best Sellers Tab ────────────────────────────────────────────────
// Manages saved Amazon Best-Seller categories. Each category has a name
// plus optional Amazon.in / Amazon.com URLs. Categories feed the
// scheduled-run picker in Amazon Market Research → Schedule Runs.
const BestSellersTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [urlIn, setUrlIn] = useState("");
  const [urlCom, setUrlCom] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bestseller-categories`);
      setRows(r.data || []);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setEditing(null); setName(""); setUrlIn(""); setUrlCom(""); setShowForm(false); };
  const openNew = () => { resetForm(); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    setName(row.name || "");
    setUrlIn(row.url_in || "");
    setUrlCom(row.url_com || "");
    setShowForm(true);
  };
  const save = async () => {
    const trimmedName = (name || "").trim();
    const trimmedIn = (urlIn || "").trim();
    const trimmedCom = (urlCom || "").trim();
    if (!trimmedName) { toast.error("Name is required"); return; }
    if (!trimmedIn && !trimmedCom) { toast.error("At least one URL (Amazon.in or Amazon.com)"); return; }
    setSaving(true);
    try {
      if (editing) {
        const r = await axios.put(`${API}/bestseller-categories/${editing.category_id}`, {
          name: trimmedName, url_in: trimmedIn, url_com: trimmedCom,
        });
        setRows((prev) => prev.map((x) => x.category_id === editing.category_id ? r.data : x));
        toast.success("Category updated");
      } else {
        const r = await axios.post(`${API}/bestseller-categories`, {
          name: trimmedName, url_in: trimmedIn, url_com: trimmedCom,
        });
        setRows((prev) => [r.data, ...prev]);
        toast.success("Category added");
      }
      resetForm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Any schedule pointing to it will also be removed.`)) return;
    try {
      await axios.delete(`${API}/bestseller-categories/${row.category_id}`);
      setRows((prev) => prev.filter((x) => x.category_id !== row.category_id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="cp-bestsellers-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Best-Seller categories</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Saved Amazon Best-Seller URLs (.in and/or .com). Referenced by Market Research → Schedule Runs.</p>
        </div>
        <Button onClick={openNew} className="bg-[#32B4AC] hover:bg-[#2a9d96] text-white text-xs" data-testid="cp-bs-add-btn">
          + Add category
        </Button>
      </div>

      {showForm && (
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3" data-testid="cp-bs-form">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Health &amp; Household" data-testid="cp-bs-name" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Amazon.in URL</label>
            <Input value={urlIn} onChange={(e) => setUrlIn(e.target.value)} placeholder="https://www.amazon.in/gp/bestsellers/…" data-testid="cp-bs-url-in" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Amazon.com URL</label>
            <Input value={urlCom} onChange={(e) => setUrlCom(e.target.value)} placeholder="https://www.amazon.com/Best-Sellers/zgbs/…" data-testid="cp-bs-url-com" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={resetForm} className="text-xs font-semibold text-gray-500 hover:text-gray-800 px-3 py-1.5" data-testid="cp-bs-cancel">Cancel</button>
            <Button onClick={save} disabled={saving} className="bg-[#32B4AC] hover:bg-[#2a9d96] text-white text-xs" data-testid="cp-bs-save">
              {saving ? "Saving…" : editing ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      )}

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        {loading ? (
          <div className="px-4 py-8 text-center text-xs text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-xs text-gray-400">
            No categories yet. Click <b>Add category</b> to save your first Best-Seller URL.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Name</th>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Amazon.in</th>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Amazon.com</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.category_id} className="hover:bg-gray-50" data-testid={`cp-bs-row-${row.category_id}`}>
                  <td className="px-3 py-2 font-semibold text-gray-900">{row.name}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-[260px] truncate" title={row.url_in || ""}>
                    {row.url_in ? (
                      <a href={row.url_in} target="_blank" rel="noreferrer" className="hover:text-[#32B4AC]">{row.url_in}</a>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600 max-w-[260px] truncate" title={row.url_com || ""}>
                    {row.url_com ? (
                      <a href={row.url_com} target="_blank" rel="noreferrer" className="hover:text-[#32B4AC]">{row.url_com}</a>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(row)}
                      className="text-[11px] font-semibold text-[#32B4AC] hover:underline mr-3"
                      data-testid={`cp-bs-edit-${row.category_id}`}
                    >Edit</button>
                    <button
                      onClick={() => remove(row)}
                      className="text-[11px] font-semibold text-rose-500 hover:underline"
                      data-testid={`cp-bs-delete-${row.category_id}`}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const StandardizationTab = () => {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attrFilter, setAttrFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editingRow, setEditingRow] = useState(null);  // { alias_id, attribute, raw_value, display_value, category }
  const [showBulk, setShowBulk] = useState(false);
  const [downloadingRaw, setDownloadingRaw] = useState(false);

  const handleDownloadRawValues = async () => {
    setDownloadingRaw(true);
    try {
      const r = await axios.get(`${API}/attribute-aliases/raw-values?days=365`);
      const values = r.data?.values || [];
      if (values.length === 0) {
        toast.info("No scraped values found yet — run a scrape first.");
        return;
      }
      // Build a CSV pre-filled for bulk import: attribute,raw_value,display_value,category
      // display_value defaults to the raw_value so unchanged rows are a no-op.
      // Values already mapped are skipped — user only sees what still needs a mapping.
      const unmapped = values.filter(v => !v.has_alias);
      if (unmapped.length === 0) {
        toast.success("All scraped values already have mappings — nothing to download.");
        return;
      }
      const header = ["attribute", "raw_value", "display_value", "category", "scraped_in_categories", "scraped_count"];
      const rows = [header];
      unmapped.forEach(v => {
        // `category` (4th col) stays empty by default — bulk-import treats
        // empty as "global alias". `scraped_in_categories` is informational —
        // shows which bestseller categories the raw value actually appeared
        // in, so the analyst can decide whether to keep the alias global or
        // copy/paste one of the values into the `category` column to scope it.
        const scrapedIn = (v.categories || []).join(" | ");
        rows.push([v.attribute, v.raw_value, v.raw_value, "", scrapedIn, v.count]);
      });
      const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `scraped-values-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${unmapped.length} unmapped values (${values.length - unmapped.length} already mapped, skipped)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download raw values");
    } finally { setDownloadingRaw(false); }
  };

  const fetchAliases = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/attribute-aliases`);
      setAliases(r.data?.aliases || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load aliases");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAliases(); }, []);

  const filtered = aliases.filter(a => {
    if (attrFilter && a.attribute !== attrFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!a.raw_value?.toLowerCase().includes(q) &&
          !a.display_value?.toLowerCase().includes(q) &&
          !(a.category || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Group by (attribute, display_value) so multiple raws collapsing to the same
  // display value read as a single semantic mapping
  const grouped = (() => {
    const m = new Map();
    filtered.forEach(a => {
      const key = `${a.attribute}|${a.display_value}|${a.category || ""}`;
      if (!m.has(key)) m.set(key, { ...a, raws: [] });
      m.get(key).raws.push(a);
    });
    return Array.from(m.values()).sort((x, y) => {
      if (x.attribute !== y.attribute) return x.attribute.localeCompare(y.attribute);
      return x.display_value.localeCompare(y.display_value);
    });
  })();

  const handleDelete = async (aliasId, label) => {
    if (!window.confirm(`Delete mapping "${label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/attribute-aliases/${aliasId}`);
      toast.success("Mapping deleted");
      fetchAliases();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleExportCsv = () => {
    const rows = [["attribute", "raw_value", "display_value", "category", "created_by_email", "updated_at"]];
    aliases.forEach(a => rows.push([
      a.attribute, a.raw_value, a.display_value, a.category || "", a.created_by_email || "", a.updated_at || ""
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attribute-aliases-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const summary = {
    total: aliases.length,
    byAttr: aliases.reduce((m, a) => { m[a.attribute] = (m[a.attribute] || 0) + 1; return m; }, {}),
  };

  return (
    <div className="space-y-5" data-testid="standardization-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Pencil className="w-5 h-5 text-indigo-500" />
            Attribute Standardization
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Map messy scraped values (e.g. <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">muscleblaze</code>, <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">MuscleBlaze</code>) to one clean display label. Applied across all Market Research analytics.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchAliases}
            disabled={loading}
            className="h-9 px-3 text-xs font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 text-slate-700 transition-all disabled:opacity-50"
            data-testid="std-refresh-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={aliases.length === 0}
            className="h-9 px-3 text-xs font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 text-slate-700 transition-all disabled:opacity-50"
            data-testid="std-export-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={handleDownloadRawValues}
            disabled={downloadingRaw}
            className="h-9 px-3 text-xs font-semibold rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 inline-flex items-center gap-1.5 text-emerald-700 transition-all disabled:opacity-50"
            title="Download every unique raw value seen across all bestseller scrapes — updates as more data is scraped"
            data-testid="std-download-raw-btn"
          >
            {downloadingRaw ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Download scraped values
          </button>
          <button
            type="button"
            onClick={() => setShowBulk(true)}
            className="h-9 px-3 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm inline-flex items-center gap-1.5 transition-all"
            data-testid="std-bulk-import-btn"
          >
            <Upload className="w-3.5 h-3.5" />
            Bulk import
          </button>
          <button
            type="button"
            onClick={() => setEditingRow({ alias_id: null, attribute: "brand", raw_value: "", display_value: "", category: "" })}
            className="h-9 px-3 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1.5 transition-all"
            data-testid="std-add-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            New mapping
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono tabular-nums">
        <span className="px-2.5 py-1 rounded-full bg-slate-900 text-white font-bold">
          {summary.total} total
        </span>
        {Object.entries(summary.byAttr).sort((a, b) => b[1] - a[1]).map(([attr, cnt]) => (
          <button
            key={attr}
            type="button"
            onClick={() => setAttrFilter(prev => prev === attr ? "" : attr)}
            className={`px-2.5 py-1 rounded-full border transition-all ${attrFilter === attr ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
            data-testid={`std-chip-${attr}`}
          >
            {ATTR_LABEL[attr] || attr} · {cnt}
          </button>
        ))}
        {attrFilter && (
          <button type="button" onClick={() => setAttrFilter("")} className="text-[10px] underline text-slate-500 hover:text-slate-700">
            clear filter
          </button>
        )}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search raw, display, or category…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
            data-testid="std-search-input"
          />
        </div>
        <select
          value={attrFilter}
          onChange={(e) => setAttrFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
          data-testid="std-attr-filter"
        >
          {ATTR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="text-[11px] font-mono tabular-nums text-slate-500">
          {grouped.length} {grouped.length === 1 ? "group" : "groups"} · {filtered.length} {filtered.length === 1 ? "mapping" : "mappings"}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-xs text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : grouped.length === 0 ? (
          <div className="py-12 text-center text-xs text-slate-400">
            {aliases.length === 0
              ? <>No mappings yet. Click <span className="font-semibold text-slate-600">"New mapping"</span> or <span className="font-semibold text-slate-600">"Bulk import"</span> to start.</>
              : "No mappings match the current filter."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="std-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5 text-left">Attribute</th>
                <th className="px-3 py-2.5 text-left">Raw values</th>
                <th className="px-3 py-2.5 text-left">Display as</th>
                <th className="px-3 py-2.5 text-left">Scope</th>
                <th className="px-3 py-2.5 text-left">Last edit</th>
                <th className="px-3 py-2.5 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grouped.map(g => (
                <tr key={`${g.attribute}|${g.display_value}|${g.category || ""}`} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    {ATTR_LABEL[g.attribute] || g.attribute}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {g.raws.map(r => (
                        <span key={r.alias_id} className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[11px] font-mono">
                          {r.raw_value}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-sm font-bold text-slate-900">{g.display_value}</td>
                  <td className="px-3 py-2.5 text-[11px]">
                    {g.category
                      ? <span className="px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold">{g.category}</span>
                      : <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold">Global</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[10px] text-slate-500 font-mono tabular-nums">
                    {g.updated_at ? new Date(g.updated_at).toLocaleDateString() : "—"}
                    {g.updated_by_email && <div className="text-[9px] text-slate-400 truncate max-w-[160px]" title={g.updated_by_email}>{g.updated_by_email}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      {g.raws.length === 1 && (
                        <button
                          type="button"
                          onClick={() => setEditingRow({ ...g.raws[0] })}
                          className="w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                          title="Edit"
                          data-testid={`std-edit-${g.raws[0].alias_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (g.raws.length === 1) handleDelete(g.raws[0].alias_id, `${g.raws[0].raw_value} → ${g.display_value}`);
                          else if (window.confirm(`Delete all ${g.raws.length} mappings under "${g.display_value}"?`)) {
                            Promise.all(g.raws.map(r => axios.delete(`${API}/attribute-aliases/${r.alias_id}`)))
                              .then(() => { toast.success(`${g.raws.length} mappings deleted`); fetchAliases(); })
                              .catch(() => toast.error("Some deletes failed"));
                          }
                        }}
                        className="w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                        title={g.raws.length === 1 ? "Delete" : `Delete ${g.raws.length} mappings`}
                        data-testid={`std-delete-${g.raws[0].alias_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit / new mapping modal */}
      {editingRow && (
        <AliasEditDialog
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={() => { setEditingRow(null); fetchAliases(); }}
        />
      )}

      {/* Bulk import modal */}
      {showBulk && (
        <BulkImportDialog
          existing={aliases}
          onClose={() => setShowBulk(false)}
          onApplied={() => { setShowBulk(false); fetchAliases(); }}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// AliasEditDialog — used for both "edit existing" and "new mapping"
// ────────────────────────────────────────────────────────────────────
const AliasEditDialog = ({ row, onClose, onSaved }) => {
  const [attribute, setAttribute] = useState(row.attribute || "brand");
  const [rawValue, setRawValue] = useState(row.raw_value || "");
  const [displayValue, setDisplayValue] = useState(row.display_value || "");
  const [category, setCategory] = useState(row.category || "");
  const [saving, setSaving] = useState(false);

  const isNew = !row.alias_id;

  const handleSave = async () => {
    if (!rawValue.trim() || !displayValue.trim()) {
      toast.error("Raw and display values are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/attribute-aliases`, {
        attribute,
        raw_value: rawValue.trim(),
        display_value: displayValue.trim(),
        category: category.trim() || null,
      });
      toast.success(isNew ? "Mapping created" : "Mapping updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="std-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-indigo-500" />
            {isNew ? "New mapping" : "Edit mapping"}
          </DialogTitle>
          <DialogDescription>
            Map a raw scraped value to a clean display label.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Attribute</label>
            <select
              value={attribute}
              onChange={(e) => setAttribute(e.target.value)}
              disabled={!isNew}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="std-edit-attribute"
            >
              {ATTR_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Raw value</label>
            <input
              type="text" value={rawValue} onChange={(e) => setRawValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 font-mono"
              placeholder="e.g. P"
              data-testid="std-edit-raw"
            />
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Display as</label>
            <input
              type="text" value={displayValue} onChange={(e) => setDisplayValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 font-semibold"
              placeholder="e.g. Powder"
              data-testid="std-edit-display"
            />
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Category scope (optional)</label>
            <input
              type="text" value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="Leave empty for Global"
              data-testid="std-edit-category"
            />
            <p className="text-[10px] text-slate-400 mt-1">Only set this if the mapping should apply within a specific category (e.g. <code>Creatine</code>). Otherwise leave blank.</p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || !rawValue.trim() || !displayValue.trim()} className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors" data-testid="std-edit-save">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : "Save mapping"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────────
// BulkImportDialog — paste CSV/TSV/excel rows with live preview.
// Accepted formats:
//   "attribute,raw,display"               → global mapping
//   "attribute,raw,display,category"      → category-scoped mapping
// Tabs, commas, semicolons all accepted as delimiters.
// ────────────────────────────────────────────────────────────────────
const _splitDelim = (line) => {
  // Smart split — handles tab, comma, semicolon
  if (line.includes("\t")) return line.split("\t");
  if (line.includes(";"))  return line.split(";");
  return line.split(",");
};

const BulkImportDialog = ({ existing, onClose, onApplied }) => {
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => setRaw(String(evt.target?.result || ""));
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsText(file);
    // Reset so user can re-upload the same file
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    const csv = [
      "attribute,raw_value,display_value,category",
      "brand,muscleblaze,MuscleBlaze,",
      "item_form,P,Powder,",
      "flavour,Choco,Chocolate,",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "standardization-template.csv";
    a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const rows = [];
    raw.split(/\r?\n/).forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      if (trimmed.startsWith("#")) return;   // skip comments
      const cells = _splitDelim(trimmed).map(c => c.trim().replace(/^"|"$/g, ""));
      if (cells.length < 3) {
        rows.push({ idx, _err: "Need at least 3 columns (attribute, raw, display)", attribute: cells[0] || "", raw_value: cells[1] || "", display_value: "" });
        return;
      }
      const [attr, rv, dv, cat] = cells;
      // Skip header row
      if (idx === 0 && /^attribute$/i.test(attr) && /^raw/i.test(rv) && /^display/i.test(dv)) return;
      const canon = _canonicalAttr(attr);
      const errors = [];
      if (!ATTR_LABEL[canon]) errors.push(`Unknown attribute "${attr}"`);
      if (!rv) errors.push("Missing raw value");
      if (!dv) errors.push("Missing display value");
      const dupExisting = (existing || []).find(a => a.attribute === canon && a.raw_lower === rv.toLowerCase() && (a.category || null) === (cat?.trim() || null));
      rows.push({
        idx,
        attribute: canon,
        attribute_raw: attr,
        raw_value: rv,
        display_value: dv,
        category: cat?.trim() || "",
        _err: errors.join(", ") || null,
        _conflict: dupExisting ? `Will overwrite "${dupExisting.display_value}"` : null,
      });
    });
    setParsed(rows);
  }, [raw, existing]);

  const validRows  = parsed.filter(r => !r._err);
  const errorRows  = parsed.filter(r => r._err);
  const conflictRows = parsed.filter(r => r._conflict && !r._err);

  const handleApply = async () => {
    if (validRows.length === 0) {
      toast.error("Nothing to import");
      return;
    }
    setSaving(true); setResult(null);
    try {
      const payload = {
        aliases: validRows.map(r => ({
          attribute: r.attribute,
          raw_value: r.raw_value,
          display_value: r.display_value,
          category: r.category || null,
        })),
      };
      const res = await axios.post(`${API}/attribute-aliases/bulk`, payload);
      setResult(res.data);
      toast.success(`${res.data.created} created · ${res.data.updated} updated`);
      // After short delay, close + refetch
      setTimeout(() => onApplied(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally { setSaving(false); }
  };

  const loadSample = () => {
    setRaw([
      "attribute,raw_value,display_value,category",
      "brand,muscleblaze,MuscleBlaze,",
      "brand,MUSCLEBLAZE,MuscleBlaze,",
      "brand,Muscle Blaze,MuscleBlaze,",
      "item_form,P,Powder,",
      "item_form,powder.,Powder,",
      "item_form,Powder Form,Powder,",
      "flavour,Choco,Chocolate,",
      "flavour,chocolate-flavor,Chocolate,",
    ].join("\n"));
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="std-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-indigo-500" />
            Bulk import mappings
          </DialogTitle>
          <DialogDescription>
            Paste rows in <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">attribute, raw_value, display_value, [category]</code> format. Tab, comma, or semicolon-delimited. Header row optional.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-2">
          {/* Left — paste */}
          <div>
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
              <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">Paste rows or upload CSV</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:underline"
                  data-testid="std-bulk-upload"
                >
                  <Upload className="w-3 h-3" />
                  Upload CSV
                </button>
                <button type="button" onClick={handleDownloadTemplate} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 hover:underline" data-testid="std-bulk-template">
                  <Download className="w-3 h-3" />
                  Template
                </button>
                <button type="button" onClick={loadSample} className="text-[10px] text-indigo-600 hover:underline font-semibold" data-testid="std-bulk-sample">
                  Insert sample
                </button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="std-bulk-file-input"
            />
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={`brand,muscleblaze,MuscleBlaze\nbrand,MUSCLEBLAZE,MuscleBlaze\nitem_form,P,Powder`}
              className="w-full h-72 px-3 py-2 text-[11.5px] font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
              data-testid="std-bulk-textarea"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Valid attributes: brand, flavour, item_form, diet_type, specialty, pack_size
            </p>
          </div>

          {/* Right — preview */}
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
              Preview ({parsed.length} row{parsed.length === 1 ? "" : "s"})
            </label>
            <div className="h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white">
              {parsed.length === 0 ? (
                <div className="py-12 text-center text-[11px] text-slate-400">
                  Paste rows on the left to preview them here.
                </div>
              ) : (
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[9.5px] font-bold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-6">#</th>
                      <th className="px-2 py-1.5 text-left">Attr</th>
                      <th className="px-2 py-1.5 text-left">Raw → Display</th>
                      <th className="px-2 py-1.5 text-left w-20">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsed.map((r, i) => (
                      <tr key={i} className={r._err ? "bg-rose-50/60" : r._conflict ? "bg-amber-50/60" : ""}>
                        <td className="px-2 py-1 text-slate-400 font-mono tabular-nums">{i + 1}</td>
                        <td className="px-2 py-1 font-mono text-[10px] text-slate-600">{r.attribute || r.attribute_raw || "—"}</td>
                        <td className="px-2 py-1">
                          <span className="font-mono text-slate-700">{r.raw_value || "—"}</span>
                          <span className="text-slate-400 mx-1">→</span>
                          <span className="font-bold text-slate-900">{r.display_value || "—"}</span>
                          {r.category && <span className="ml-1 text-[9px] text-amber-700 font-semibold">@{r.category}</span>}
                        </td>
                        <td className="px-2 py-1">
                          {r._err
                            ? <span className="inline-flex items-center gap-0.5 text-rose-700 font-semibold" title={r._err}><AlertCircle className="w-3 h-3" /> error</span>
                            : r._conflict
                            ? <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold" title={r._conflict}><RefreshCw className="w-3 h-3" /> update</span>
                            : <span className="inline-flex items-center gap-0.5 text-emerald-700 font-semibold"><Plus className="w-3 h-3" /> new</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] font-mono tabular-nums">
              <span className="text-emerald-700 font-bold">{validRows.length - conflictRows.length} new</span>
              <span className="text-amber-700 font-bold">{conflictRows.length} update</span>
              <span className="text-rose-700 font-bold">{errorRows.length} errors</span>
            </div>
          </div>
        </div>

        {result && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[11px] text-emerald-800">
            ✓ Imported · {result.created} created · {result.updated} updated
            {result.errors?.length > 0 && <span className="text-rose-700 ml-2">· {result.errors.length} errors</span>}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={handleApply} disabled={saving || validRows.length === 0} className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-1.5" data-testid="std-bulk-apply">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {saving ? "Importing…" : `Apply ${validRows.length} mapping${validRows.length === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BASE_TABS = [
  { id: "brands", label: "Brands" },
  { id: "products", label: "Products" },
  { id: "platforms", label: "Platforms" },
  { id: "pincodes", label: "Pincodes" },
  { id: "keywords", label: "Keywords" },
  { id: "bestsellers", label: "Best Sellers" },
  { id: "standardization", label: "Standardization", perm: "manage_attributes" },
];

export const ControlPanel = ({ user, onNavigate, onLogout }) => {
  const [platforms, setPlatforms] = useState([]);
  const [activeTab, setActiveTab] = useState("brands");
  const [proxyConfig, setProxyConfig] = useState(null);
  const userPerms = user?.permissions || [];
  const isAdmin = user?.is_admin || userPerms.includes("manage_users");
  const canManageAttr = isAdmin || userPerms.includes("manage_attributes");

  // Filter base tabs by permission, then append admin-only Users tab
  const visibleBase = BASE_TABS.filter(t => !t.perm || isAdmin || userPerms.includes(t.perm));
  const TAB_ITEMS = isAdmin ? [...visibleBase, { id: "users", label: "Users" }] : visibleBase;

  const fetchPlatforms = async () => {
    try {
      const r = await axios.get(`${API}/platforms`);
      setPlatforms(r.data || []);
    } catch {}
  };

  const fetchProxyConfig = async () => {
    try {
      const r = await axios.get(`${API}/scrape/config`);
      setProxyConfig(r.data);
    } catch {}
  };

  useEffect(() => {
    fetchPlatforms();
    fetchProxyConfig();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        user={user}
        breadcrumbs={(() => {
          const TAB_META = {
            brands:          { label: 'Brands',          icon: <Tag className="w-3.5 h-3.5" /> },
            products:        { label: 'Products',        icon: <Package className="w-3.5 h-3.5" /> },
            platforms:       { label: 'Platforms',       icon: <Layers className="w-3.5 h-3.5" /> },
            pincodes:        { label: 'Pincodes',        icon: <MapPin className="w-3.5 h-3.5" /> },
            keywords:        { label: 'Keywords',        icon: <Sparkles className="w-3.5 h-3.5" /> },
            bestsellers:     { label: 'Best Sellers',    icon: <Sparkles className="w-3.5 h-3.5" /> },
            standardization: { label: 'Standardization', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
            users:           { label: 'Users',           icon: <Users className="w-3.5 h-3.5" /> },
          };
          const active = TAB_META[activeTab];
          return [
            { label: "Platforms", onClick: () => onNavigate("/select-platform"), testId: "platforms" },
            {
              label: "Control Panel",
              icon: <Package className="w-3.5 h-3.5" />,
              onClick: activeTab !== 'brands' ? () => setActiveTab('brands') : undefined,
              testId: "control-panel",
            },
            ...(active ? [{ label: active.label, icon: active.icon, testId: `cp-${activeTab}` }] : []),
          ];
        })()}
        onNavigate={onNavigate}
        onLogout={onLogout}
        rightContent={
          <div className="flex items-center gap-2">
            {proxyConfig && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200" data-testid="proxy-status">
                <Shield className="w-3.5 h-3.5 text-[#2EB4AC]" />
                {proxyConfig.proxy_configured ? (
                  <>
                    <span className="text-xs text-gray-600">{proxyConfig.proxy_display}</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                  </>
                ) : (
                  <span className="text-xs text-red-500">No proxy</span>
                )}
              </div>
            )}
            {isAdmin && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase">Admin</span>
            )}
          </div>
        }
      />

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-1 mb-6 bg-white border border-gray-200 p-1 rounded-full w-fit" data-testid="control-panel-tabs">
          {TAB_ITEMS.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#2EB4AC] text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === "brands" && <BrandsTab platforms={platforms} />}
        {activeTab === "products" && <ProductsTab platforms={platforms} />}
        {activeTab === "platforms" && <PlatformsTab platforms={platforms} onPlatformsChange={fetchPlatforms} />}
        {activeTab === "pincodes" && <PincodesTab />}
        {activeTab === "keywords" && <KeywordsTab />}
        {activeTab === "bestsellers" && <BestSellersTab />}
        {activeTab === "standardization" && canManageAttr && <StandardizationTab />}
        {activeTab === "users" && isAdmin && <UsersTab />}
      </main>
    </div>
  );
};

export default ControlPanel;
